/**
 * ConferenceConnector: Jitsi 회의 룸 생명주기 및 이벤트 관리
 *
 * 기존 wehago-conference의 SVConference/ConferenceConnector.js (2021줄)를
 * TypeScript로 리팩토링. 핵심 역할:
 *
 * 1. JitsiConference 생성 및 입장
 * 2. 로컬/원격 트랙 이벤트 핸들링
 * 3. 참여자 입/퇴장 이벤트 핸들링
 * 4. 룸 커맨드 송수신 (발언권, 그리기 등)
 * 5. 트랙 생성/교체/dispose (jitsi-meet 참조 구현 기반)
 */
import type {
  JitsiConnection,
  JitsiConference,
  JitsiLocalTrack,
  JitsiRemoteTrack,
  JitsiParticipant,
} from '@/types/jitsi';
import type { ConferenceHandlers } from './types';
import { participantsActions } from '@/store/slices/participantsSlice';
import { audioIndicatorActions } from '@/store/slices/audioIndicatorSlice';
import { audioLevelStore } from '@/stores/audioLevelStore';
import { mainUserActions } from '@/store/slices/mainUserSlice';
import { masterActions, type MasterUser } from '@/store/slices/masterSlice';
import { chatActions } from '@/store/slices/chatSlice';
import { captionActions } from '@/store/slices/captionSlice';
import { recordingActions } from '@/store/slices/recordingSlice';
import { floorActions } from '@/store/slices/floorSlice';
import { screenShareActions } from '@/store/slices/screenShareSlice';
import { voiceNoteActions } from '@/store/slices/voiceNoteSlice';
import { localActions } from '@/store/slices/localSlice';
import { translationActions, type TranslationLanguage } from '@/store/slices/translationSlice';
import { drawingActions } from '@/store/slices/drawingSlice';
import { documentShareActions } from '@/store/slices/documentShareSlice';
import { whiteboardActions } from '@/store/slices/whiteboardSlice';
import { uiActions } from '@/store/slices/uiSlice';
import { store } from '@/store';
import { showToast } from '@/utils/globalToast';
import { safeDisposeTrack } from '@/utils/track';
import { NoiseSuppressionEffect } from '@/utils/noiseSuppressionEffect';
import { WHITEBOARD_ID, METADATA_KEYS } from '@/utils/whiteboard/constants';
import { stopWhiteboardSession } from '@/utils/whiteboard';
import { toAbsoluteProfileUrl, toDisplayProfileUrl } from '@/utils/profileUtils';
import config from './config';
import { isRecorderMode, recorderStatsId } from '@/utils/recorderMode';
import { getGridResolution, FOCUS_VIEW_POLICY, type GridTier, type VideoQualityLevel } from './config/videoQualityPolicy';
import { CONFERENCE_COMMANDS } from './commands';
import { SYSTEM_MSG, saveMessageToDB } from './systemMessages';
import { translateOpenAI } from '@/api/translateAPI';
import { JigasiPartialTranslator } from './JigasiPartialTranslator';

// 오디오 레벨 이벤트를 너무 자주 dispatch하지 않기 위한 쓰로틀 간격 (ms)
const AUDIO_LEVEL_THROTTLE_MS = 200;
const JIGASI_FEATURE = 'http://jitsi.org/protocol/jigasi';

 // recorder(Jibri) 엔드포인트가 JVB에 광고하는 가정 대역폭.
const RECORDER_ASSUMED_BANDWIDTH_BPS = 20_000_000;
const TRANSCRIBER_FEATURE = 'http://jitsi.org/protocol/transcriber';
const TRANSCRIBER_STATUS_ON = 'ON';
const TRANSCRIBER_STATUS_OFF = 'OFF';
const TRANSCRIBER_STATUS_RECHECK_DELAY_MS = 1000;
const TRANSCRIBER_STATUS_RECHECK_ATTEMPTS = 5;

type WehagoIdentityAttributes = Record<string, string>;

interface CommandValuesWithAttributes {
  value?: unknown;
  attributes?: Record<string, unknown>;
}

class ConferenceConnector {
  private _room: JitsiConference | null = null;
  private _roomId: string = '';
  private _handlers: ConferenceHandlers | null = null;
  private _localAudioTrack: JitsiLocalTrack | null = null;
  private _localVideoTrack: JitsiLocalTrack | null = null;
  private _localScreenTrack: JitsiLocalTrack | null = null;
  private _userInfo: Record<string, string> = {};
  private _wehagoIdentityAttributes: WehagoIdentityAttributes | null = null;
  private _audioLevelTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private _lastAudioLevelDispatch: Map<string, number> = new Map();
  // 원격 비디오 트랙 sourceName 맵: participantId → sourceName (quality 조절에 사용)
  private _remoteVideoSourceNames: Map<string, string> = new Map();
  // 원격 화면공유 트랙 sourceName 맵: participantId → sourceName (quality 조절에 사용)
  private _remoteScreenSourceNames: Map<string, string> = new Map();
  // 노이즈 제거 효과 활성 여부
  private _noiseSuppressionEnabled: boolean = true;
  private _lastRecordingStatus: 'idle' | 'pending' | 'on' | 'off' | null = null;
  /** 같은 세션의 같은 녹화 에러가 presence 재전송으로 반복 통지되는 것을 막는다 */
  private _lastRecordingErrorKey: string | null = null;
  // Jigasi interim 부분 번역기
  private _partialTranslator: JigasiPartialTranslator | null = null;
  // Jigasi(Transcriber) 등 hidden 참여자 ID 블랙리스트 — TRACK_ADDED 재진입 방지
  private _hiddenParticipantIds: Set<string> = new Set();
  private _transcriberParticipantIds: Set<string> = new Set();
  private _transcriberStatusSyncTimer: ReturnType<typeof setTimeout> | null = null;
  private _transcriberStatusSyncAttempts: number = 0;

  // ConferenceManager에서 주입하는 TrackContext 업데이트 콜백
  onRemoteTrackAdded?: (participantId: string, track: JitsiRemoteTrack) => void;
  onRemoteTrackRemoved?: (participantId: string, track: JitsiRemoteTrack) => void;
  onParticipantLeft?: (participantId: string) => void;

  private _getReceiverQualityOverrides(
    screenMode: 'GRID' | 'SIDE' | 'PRESENTER',
    ov: ReturnType<typeof store.getState>['ui']['videoQualityOverride'],
  ): Parameters<ConferenceConnector['updateReceiverQuality']>[3] {
    if (!ov) return undefined;

    if (screenMode === 'GRID') {
      return (ov.grid != null || ov.gridTiers != null || ov.gridFallback != null)
        ? { main: ov.grid, gridTiers: ov.gridTiers, gridFallback: ov.gridFallback }
        : undefined;
    }

    if (screenMode === 'PRESENTER') {
      return (ov.presenterMain != null || ov.presenterSub != null)
        ? { main: ov.presenterMain, sub: ov.presenterSub }
        : undefined;
    }

    return (ov.sideMain != null || ov.sideSub != null)
      ? { main: ov.sideMain, sub: ov.sideSub }
      : undefined;
  }

  private _reapplyCurrentReceiverQuality(): void {
    const state = store.getState();
    const ui = state.ui;
    const remoteCount = state.participants.participantIds.length;
    // recorder 모드는 자기 자신 타일을 그리지 않으므로(+1 슬롯 불필요) 원격 인원수만 사용
    const visibleCount = ui.screenMode === 'GRID'
      ? (isRecorderMode() ? Math.min(remoteCount, ui.maxGridSize) : Math.min(remoteCount + 1, ui.maxGridSize))
      : remoteCount;

    this.updateReceiverQuality(
      ui.screenMode,
      state.mainUser.pinnedUserId ?? state.mainUser.mainUserId,
      visibleCount,
      this._getReceiverQualityOverrides(ui.screenMode, ui.videoQualityOverride),
    );
  }

  private _isAiTranslationModeEnabled(): boolean {
    return store.getState().local.aiTranslationType != null;
  }

  private _shouldUseJigasiSttResults(): boolean {
    return this.isJigasiTranscriberActive();
  }

  private _toAttributeString(value: unknown, fallback = ''): string {
    return value == null ? fallback : String(value);
  }

  private _normalizeWehagoIdentityOverrides(
    attributes?: Record<string, unknown>
  ): WehagoIdentityAttributes {
    if (!attributes) return {};

    return Object.entries(attributes).reduce<WehagoIdentityAttributes>((acc, [key, value]) => {
      acc[key] = this._toAttributeString(value);
      return acc;
    }, {});
  }

  private _createBaseWehagoIdentityAttributes(): WehagoIdentityAttributes {
    const state = store.getState();
    const local = state.local;
    let currentTranslationMode: unknown;
    try {
      currentTranslationMode = this._room?.getLocalParticipantProperty('isTranslationMode');
    } catch {
      // room이 dispose된 zombie 상태일 수 있음 (presMap === null)
    }
    const isTranslationMode = this._toAttributeString(
      currentTranslationMode,
      this._isAiTranslationModeEnabled() ? 'T' : (this._userInfo.isTranslationMode ?? 'F'),
    );

    return {
      wehagoId: this._userInfo.wehagoId ?? local.wehagoId ?? '',
      nickname: this._userInfo.nickname ?? local.nickname ?? '',
      companyCode: this._userInfo.companyCode ?? local.companyCode ?? '',
      avatarUrl: this._userInfo.avatarUrl ?? local.avatarUrl ?? '',
      selectedAvatar: this._userInfo.selectedAvatar ?? local.selectedAvatar ?? '',
      nationality: this._userInfo.nationality ?? local.nationality ?? state.translation.language,
      // v1 호환 필드
      userIdentificationCode: this._userInfo.userIdentificationCode ?? '',
      companyFullpath: this._userInfo.companyFullpath ?? '',
      profile_url: this._userInfo.profileUrl ?? this._userInfo.avatarUrl ?? local.profileUrl ?? '',
      isExternalParticipant: this._userInfo.isExternalParticipant ?? String(local.isGuest),
      userName: this._userInfo.userName2 ?? local.userName ?? '',
      avatar: this._userInfo.avatar ?? '',
      externalUserId: this._userInfo.externalUserId ?? '',
      user_contact: this._userInfo.userContact ?? '',
      user_email: this._userInfo.userEmail ?? '',
      is_screen: this._userInfo.isScreen ?? 'false',
      user_no: this._userInfo.userNo ?? local.userNo ?? '',
      user_type: this._userInfo.userType ?? '',
      host: this._userInfo.host ?? 'false',
      isTranslationMode,
      enableAutoLang: state.translation.isAutoLangEnabled ? 'true' : 'false',
      voiceNoteLocale: this._userInfo.voiceNoteLocale ?? '',
      employee_no: this._userInfo.employeeNo ?? local.employeeNo ?? '',
      company_no: this._userInfo.companyNo ?? local.companyNo ?? '',
    };
  }

  private _buildWehagoIdentityAttributes(
    overrides?: Record<string, unknown>
  ): WehagoIdentityAttributes {
    const attributes = {
      ...this._createBaseWehagoIdentityAttributes(),
      ...(this._wehagoIdentityAttributes ?? {}),
      ...this._normalizeWehagoIdentityOverrides(overrides),
    };
    this._wehagoIdentityAttributes = attributes;
    return attributes;
  }

  private _mergeWehagoIdentityAttributes(overrides: Record<string, unknown>): void {
    this._buildWehagoIdentityAttributes(overrides);
  }

  private _isCommandValuesWithAttributes(values: unknown): values is CommandValuesWithAttributes {
    return values != null && typeof values === 'object' && !Array.isArray(values);
  }

  private _sendWehagoIdentityCommand(values: unknown): void {
    const room = this._room;
    if (!room) return;

    const payload = this._isCommandValuesWithAttributes(values) ? values : {};
    const attributes = this._buildWehagoIdentityAttributes(payload.attributes);
    const value = this._toAttributeString(payload.value, room.myUserId());

    room.sendCommand(CONFERENCE_COMMANDS.WEHAGO_ID, {
      ...payload,
      value,
      attributes,
    });
  }

  private _normalizeTranscriptionStatus(status: unknown): string {
    return status == null ? '' : String(status).toUpperCase();
  }

  private _getCurrentTranscriptionStatus(): string {
    if (!this._room) return '';
    try {
      return this._normalizeTranscriptionStatus(this._room.getTranscriptionStatus?.());
    } catch {
      return '';
    }
  }

  private _isTruthyParticipantProperty(value: unknown): boolean {
    return value === true || value === 'true' || value === 'T' || value === 1 || value === '1';
  }

  private _participantHasFeature(participant: JitsiParticipant, feature: string): boolean {
    try {
      return participant.hasFeature(feature) === true;
    } catch {
      return false;
    }
  }

  private _isJigasiTranscriberParticipant(participant: JitsiParticipant): boolean {
    const hasTranscriberFeature = this._participantHasFeature(participant, TRANSCRIBER_FEATURE);
    if (hasTranscriberFeature) return true;

    const hasJigasiFeature = this._participantHasFeature(participant, JIGASI_FEATURE)
      || this._isTruthyParticipantProperty(participant.getProperty('features_jigasi'));

    const participantWithOptionalMethods = participant as JitsiParticipant & {
      getJid?: () => string;
      getBotType?: () => string;
    };
    const identityText = [
      participant.getId(),
      participant.getDisplayName?.(),
      participantWithOptionalMethods.getJid?.(),
      participantWithOptionalMethods.getBotType?.(),
      participant.getProperty('botType'),
      participant.getProperty('statsId'),
    ]
      .filter((value) => value != null)
      .map(String)
      .join('|')
      .toLowerCase();

    if (/transcriber|jitsi_meet_transcribe/.test(identityText)) return true;

    return hasJigasiFeature && participant.isHidden();
  }

  private _findExistingJigasiTranscriber(): JitsiParticipant | null {
    if (!this._room) return null;

    try {
      return this._room.getParticipants().find((participant) => (
        this._isJigasiTranscriberParticipant(participant)
      )) ?? null;
    } catch {
      return null;
    }
  }

  private _clearTranscriberStatusSyncTimer(): void {
    if (this._transcriberStatusSyncTimer) {
      clearTimeout(this._transcriberStatusSyncTimer);
      this._transcriberStatusSyncTimer = null;
    }
    this._transcriberStatusSyncAttempts = 0;
  }

  private _markTranscriberJoined(handlers: ConferenceHandlers, jid: string | null): void {
    this._clearTranscriberStatusSyncTimer();
    if (jid) {
      this._transcriberParticipantIds.add(jid);
      const resource = jid.includes('/') ? jid.slice(jid.lastIndexOf('/') + 1) : jid;
      if (resource) this._transcriberParticipantIds.add(resource);
    }
    handlers.dispatch(captionActions.setTranscriberDialPending(false));
    handlers.dispatch(captionActions.setTranscriberJoined({ joined: true, jid }));
  }

  private _markTranscriberLeft(handlers: ConferenceHandlers): void {
    this._clearTranscriberStatusSyncTimer();
    this._transcriberParticipantIds.clear();
    handlers.dispatch(captionActions.setTranscriberDialPending(false));
    handlers.dispatch(captionActions.setTranscriberJoined({ joined: false, jid: null }));
  }

  private _markExistingJigasiTranscriberJoined(handlers: ConferenceHandlers): boolean {
    const transcriber = this._findExistingJigasiTranscriber();
    if (!transcriber) return false;

    const transcriberId = transcriber.getId();
    this._hiddenParticipantIds.add(transcriberId);
    this._markTranscriberJoined(handlers, transcriberId);
    return true;
  }

  private _syncCurrentTranscriberState(handlers: ConferenceHandlers): boolean {
    if (this._markExistingJigasiTranscriberJoined(handlers)) return true;

    const status = this._getCurrentTranscriptionStatus();
    if (!status || status === TRANSCRIBER_STATUS_OFF) return false;

    if (status === TRANSCRIBER_STATUS_ON) {
      this._markTranscriberJoined(handlers, null);
    } else {
      handlers.dispatch(captionActions.setTranscriberDialPending(true));
    }
    return true;
  }

  isJigasiTranscriberActive(): boolean {
    return store.getState().caption.isTranscriberJoined
      || this._getCurrentTranscriptionStatus() === TRANSCRIBER_STATUS_ON
      || this._findExistingJigasiTranscriber() != null;
  }

  private _scheduleTranscriberStatusSync(handlers: ConferenceHandlers): void {
    if (this._transcriberStatusSyncTimer) return;
    if (store.getState().caption.isTranscriberJoined) return;

    this._transcriberStatusSyncTimer = setTimeout(() => {
      this._transcriberStatusSyncTimer = null;

      if (!this._room) {
        handlers.dispatch(captionActions.setTranscriberDialPending(false));
        return;
      }

      if (this._syncCurrentTranscriberState(handlers)) {
        if (!store.getState().caption.isTranscriberJoined) {
          this._scheduleTranscriberStatusSync(handlers);
        }
        return;
      }

      this._transcriberStatusSyncAttempts += 1;
      if (this._transcriberStatusSyncAttempts < TRANSCRIBER_STATUS_RECHECK_ATTEMPTS) {
        this._scheduleTranscriberStatusSync(handlers);
        return;
      }

      handlers.dispatch(captionActions.setTranscriberDialPending(false));
      this._transcriberStatusSyncAttempts = 0;
    }, TRANSCRIBER_STATUS_RECHECK_DELAY_MS);
  }

  // ─── 연결 ────────────────────────────────────────────────────────────────

  async connect(
    jitsiConnection: JitsiConnection,
    roomName: string,
    userName: string,
    userInfo: Record<string, string>,
    handlers: ConferenceHandlers
  ): Promise<void> {
    this._handlers = handlers;
    this._roomId = roomName;
    this._userInfo = {
      ...userInfo,
      profileUrl: toAbsoluteProfileUrl(userInfo.profileUrl) ?? '',
      avatarUrl: userInfo.avatarUrl ?? '',
    };

    // 회의 룸 생성
    const room = jitsiConnection.initJitsiConference(
      roomName.toLowerCase(),
      {
        ...config,
        // recorder(Jibri) 모드면 stats-id를 recorder 봇 값으로 광고한다.
        // lib-jitsi-meet이 config.statisticsId를 presence <stats-id>로 싣고,
        // 다른 참가자는 getStatsID()로 이를 읽어 recorder를 UI에서 숨긴다
        // (_handleUserJoined / utils/recordingBot.ts). 일반 참여자면 미설정.
        ...(isRecorderMode() ? { statisticsId: recorderStatsId() } : {}),
        // DisplayName은 입장 후 setDisplayName으로도 설정 가능하지만
        // participant properties로도 전달
      }
    );
    this._room = room;

    // 이벤트 바인딩 (입장 전에 해야 함)
    this._bindConferenceEvents(room, handlers);
    this._bindCommandListeners(room, handlers);

    // 로컬 트랙 생성 후 추가 (초기 설정 반영)
    await this._createAndAddLocalTracks(handlers, {
      startVideoMuted: userInfo.startVideoMuted === 'true',
      startAudioMuted: userInfo.startAudioMuted === 'true',
      audioInputDeviceId: userInfo.audioInputDeviceId,
      videoInputDeviceId: userInfo.videoInputDeviceId,
    });

    // 사용자 속성 설정 (wehagoId, companyCode, avatarUrl, nationality 등)
    Object.entries(userInfo).forEach(([key, value]) => {
      room.setLocalParticipantProperty(key, value);
    });

    room.setDisplayName(userName);

    // 입장
    room.join();
  }

  // ─── 로컬 트랙 생성 ──────────────────────────────────────────────────────

  /**
   * 오디오/비디오 로컬 트랙을 독립적으로 생성하고 회의에 추가한다.
   *
   * lib-jitsi-meet 패턴 (react/features/base/tracks/actions.web.ts) 준수:
   * - 오디오/비디오를 별도로 생성 → 한쪽 권한 거부/장치 없음이어도 다른 쪽은 정상 진행
   * - JitsiTrackErrors 에러 코드로 사용자 안내 분기
   * - 비디오 UNSUPPORTED_RESOLUTION → 해상도 제약 없이 재시도
   */
  private async _createAndAddLocalTracks(
    handlers: ConferenceHandlers,
    options?: {
      startVideoMuted?: boolean;
      startAudioMuted?: boolean;
      audioInputDeviceId?: string;
      videoInputDeviceId?: string;
    }
  ): Promise<void> {

    // 오디오 생성 (항상 하지만 처음 시작 시 뮤트는 처리)
    try {
      const audioCreateOptions: { devices: ['audio']; micDeviceId?: string } = { devices: ['audio'] };
      if (options?.audioInputDeviceId) {
        audioCreateOptions.micDeviceId = options.audioInputDeviceId;
      }
      const audioTracks = await window.JitsiMeetJS.createLocalTracks(audioCreateOptions);
      for (const track of audioTracks) {
        if (options?.startAudioMuted) {
          await track.mute();
        }
        await this._addLocalTrack(track, handlers);

        // 노이즈 제거 효과 자동 적용
        if (this._noiseSuppressionEnabled) {
          await this._applyNoiseSuppression(track);
        }

        const deviceId = options?.audioInputDeviceId || track.getDeviceId?.();
        if (deviceId) {
          handlers.dispatch(localActions.setAudioInputDeviceId(deviceId));
        }
      }
    } catch (err: unknown) {
      const errName = (err as { name?: string })?.name ?? '';
      if (errName === 'gum.permission_denied') {
        console.warn('[ConferenceConnector] 마이크 권한이 거부되었습니다.');
      } else if (errName === 'gum.not_found') {
        console.warn('[ConferenceConnector] 마이크 장치를 찾을 수 없습니다.');
      } else {
        console.warn('[ConferenceConnector] 오디오 트랙 생성 실패:', errName);
      }
    }

    // 비디오 생성 (카메라를 사용하는 경우에만 생성)
    if (!options?.startVideoMuted) {
      try {
        const videoCreateOptions: {
          devices: ['video'];
          cameraDeviceId?: string;
          resolution: number;
          constraints: typeof config.constraints;
        } = {
          devices: ['video'],
          resolution: 720,
          constraints: config.constraints,
        };
        if (options?.videoInputDeviceId) {
          videoCreateOptions.cameraDeviceId = options.videoInputDeviceId;
        }
        const videoTracks = await window.JitsiMeetJS.createLocalTracks(videoCreateOptions);
        for (const track of videoTracks) {
          await this._addLocalTrack(track, handlers);

          const deviceId = options?.videoInputDeviceId || track.getDeviceId?.();
          if (deviceId) {
            handlers.dispatch(localActions.setVideoInputDeviceId(deviceId));
          }
        }
      } catch (err: unknown) {
        const errName = (err as { name?: string })?.name ?? '';
        if (errName === 'gum.unsupported_resolution') {
          console.warn('[ConferenceConnector] 비디오 해상도 미지원 — 기본 해상도로 재시도');
          try {
            const fallbackTracks = await window.JitsiMeetJS.createLocalTracks({
              devices: ['video'],
            });
            for (const track of fallbackTracks) {
              await this._addLocalTrack(track, handlers);
            }
          } catch (fallbackErr) {
            console.warn('[ConferenceConnector] 비디오 재시도 실패:', fallbackErr);
          }
        } else if (errName === 'gum.permission_denied') {
          console.warn('[ConferenceConnector] 카메라 권한이 거부되었습니다.');
        } else if (errName === 'gum.not_found') {
          console.warn('[ConferenceConnector] 카메라 장치를 찾을 수 없습니다.');
        } else {
          console.warn('[ConferenceConnector] 비디오 트랙 생성 실패:', errName);
        }
      }
    } else {
      console.log('[ConferenceConnector] 비디오 음소거(아바타 사용/카메라 설정 안함) 상태로 시작하므로 비디오 트랙 생략');
    }
  }

  private async _addLocalTrack(
    track: JitsiLocalTrack,
    handlers: ConferenceHandlers
  ): Promise<void> {
    const mediaType = track.getType();

    if (mediaType === 'audio') {
      // 이전 로컬 오디오 트랙 리스너 정리
      if (this._localAudioTrack) {
        this._localAudioTrack.removeAllListeners(
          window.JitsiMeetJS.events.track.TRACK_AUDIO_LEVEL_CHANGED
        );
      }
      this._localAudioTrack = track;
      handlers.onLocalAudioTrackAdded(track);

      // 로컬 오디오 레벨 리스너 (conference-level 이벤트는 로컬 사용자에게 발생하지 않을 수 있음)
      track.on(
        window.JitsiMeetJS.events.track.TRACK_AUDIO_LEVEL_CHANGED,
        (audioLevel: number) => {
          if (track.isMuted()) return;
          const localJitsiId = this._room?.myUserId();
          if (localJitsiId) {
            this._handleAudioLevel(localJitsiId, audioLevel, handlers);
          }
        }
      );
    } else if (mediaType === 'video') {
      this._localVideoTrack = track;
      handlers.onLocalVideoTrackAdded(track);
    }

    if (this._room) {
      try {
        await this._room.addTrack(track);
      } catch (err) {
        console.warn('[ConferenceConnector] addTrack error:', err);
      }
    }
  }

  // ─── 이벤트 바인딩 ────────────────────────────────────────────────────────

  private _bindConferenceEvents(
    room: JitsiConference,
    handlers: ConferenceHandlers
  ): void {
    const { events } = window.JitsiMeetJS;
    const E = events.conference;

    room.on(E.CONFERENCE_JOINED, () => {
      handlers.onConferenceJoined();
      // Jitsi 참여자 ID 저장 (채팅 isMe 판별용)
      handlers.dispatch(localActions.setJitsiId(room.myUserId()));
      this._saveMyJitsiId(room.myUserId());
      // 디버깅용 글로벌 노출
      (window as any)._jitsiRoom = room;

      // recorder 모드: JVB BWE 고착으로 인한 소스 suspend 방지 (RECORDER_ASSUMED_BANDWIDTH_BPS 주석 참고)
      if (isRecorderMode()) {
        (room as unknown as { setAssumedBandwidthBps?: (bps: number) => void })
          .setAssumedBandwidthBps?.(RECORDER_ASSUMED_BANDWIDTH_BPS);
      }
      // 초기 moderator 상태 설정
      const isMod = room.isModerator();
      if (isMod === true) {
        handlers.dispatch(localActions.setModerator(true));
      }
      this._syncCurrentTranscriberState(handlers);
      const enableAutoLang = store.getState().translation.isAutoLangEnabled ? 'true' : 'false';
      // wehagoid 커맨드 전송 (닉네임, 사용자 정보 공유)
      this.sendCommand(CONFERENCE_COMMANDS.WEHAGO_ID, {
        value: room.myUserId(),
      });

      // 사용자 타입 설정 (위하고 유저: 1, 외부 유저: 2)
      const wehagoId = this._userInfo.wehagoId ?? '';
      const externalUserId = this._userInfo.externalUserId ?? '';
      room.setLocalParticipantProperty('userType', wehagoId ? 1 : 2);

      // 사용자 접속시간 설정
      room.setLocalParticipantProperty('joinedAt', Date.now());

      // 사용자 ID 설정 (위하고ID 또는 외부유저 ID — 마스터 판별 핵심)
      room.setLocalParticipantProperty('user', wehagoId || externalUserId);

      // 동시접속 차단 여부
      room.setLocalParticipantProperty('blockSimultaneousAccess', 'true');

      // STT 자동언어인식 여부. 실제 인식은 Jigasi/STT 서버가 처리한다.
      room.setLocalParticipantProperty('enableAutoLang', enableAutoLang);

      // ── 중복 접속자(이전 세션) 강제 퇴장 처리 ──
      const localUserProp = wehagoId || externalUserId;
      if (localUserProp) {
        // 내 환경이 동시접속을 차단하는 환경인지 확인
        const isLocalBlock = room.getLocalParticipantProperty('blockSimultaneousAccess') !== 'false';

        room.getParticipants().forEach((p) => {
          if (p.getProperty('user') === localUserProp) {
            // 상대방(이전 세션)이 동시접속을 차단하는 환경인지 확인
            const isRemoteBlock = p.getProperty('blockSimultaneousAccess') !== 'false';

            // 둘 다 동시접속 차단 환경일 때만 킥 (모바일 등이 섞여서 false가 있다면 킥하지 않음)
            if (isLocalBlock && isRemoteBlock) {
              this.sendCommand(CONFERENCE_COMMANDS.SET_DELETE_SAME_PARTICIPANT, {
                value: room.myUserId(),
                attributes: {
                  participantId: p.getId(),
                },
              });
            }
          }
        });
      }

      // ── Jigasi transcription: 참여자별 언어 속성 설정 + 초대 ──
      const state = store.getState();
      this.syncTranscriptionMode(
        state.caption.isEnabled || state.translation.isCaptionEnabled || state.translation.isTranslateEnabled,
        state.translation.language,
      );
    });
    room.on(E.CONFERENCE_FAILED, (errorCode: unknown) =>
      handlers.onConferenceFailed(String(errorCode))
    );
    room.on(E.CONFERENCE_LEFT, () => handlers.onConferenceLeft());

    // 원격 트랙
    room.on(E.TRACK_ADDED, (track: unknown) => {
      const t = track as JitsiRemoteTrack;
      if (t.isLocal()) return; // 로컬 트랙은 이미 처리됨
      this._handleRemoteTrackAdded(t, handlers);
    });

    room.on(E.TRACK_REMOVED, (track: unknown) => {
      const t = track as JitsiRemoteTrack;
      if (t.isLocal()) return;
      this._handleRemoteTrackRemoved(t, handlers);
    });

    room.on(E.TRACK_MUTE_CHANGED, (track: unknown) => {
      const t = track as JitsiLocalTrack | JitsiRemoteTrack;
      this._handleTrackMuteChanged(t, handlers);
    });

    // 오디오 레벨 (쓰로틀 처리)
    room.on(E.TRACK_AUDIO_LEVEL_CHANGED, (userId: unknown, audioLevel: unknown) => {
      this._handleAudioLevel(String(userId), Number(audioLevel), handlers);
    });

    // 참여자
    room.on(E.USER_JOINED, (id: unknown, participant: unknown) => {
      this._handleUserJoined(String(id), participant as JitsiParticipant, handlers);
    });

    room.on(E.USER_LEFT, (id: unknown) => {
      this._handleUserLeft(String(id), handlers);
    });

    room.on(E.PARTICIPANT_PROPERTY_CHANGED, (
      participant: unknown,
      name: unknown,
      _oldValue: unknown,
      newValue: unknown
    ) => {
      const p = participant as JitsiParticipant;
      const propName = String(name);
      const id = p.getId();
      handlers.dispatch(
        participantsActions.participantPropertyChanged({
          id,
          name: propName,
          value: newValue,
        })
      );
      if (
        propName === 'features_jigasi'
        && this._isTruthyParticipantProperty(newValue)
      ) {
        this._hiddenParticipantIds.add(id);
        this._markTranscriberJoined(handlers, id);
      }
      // nickname / wehagoId 속성 변경 시 Participant 필드도 직접 업데이트
      if (propName === 'nickname') {
        handlers.dispatch(
          participantsActions.participantUpdated({ id, nickname: String(newValue ?? '') })
        );
      }
      if (propName === 'wehagoId') {
        handlers.dispatch(
          participantsActions.participantUpdated({ id, wehagoId: String(newValue ?? '') })
        );
      }
    });

    // recorder(Jibri)는 발언자 자동전환을 자체적으로 하지 않고, 방생성자의 화면
    room.on(E.DOMINANT_SPEAKER_CHANGED, (id: unknown) => {
      const speakerId = String(id);
      handlers.dispatch(audioIndicatorActions.setDominantSpeaker(speakerId));
      if (isRecorderMode()) return;
      const { ui, mainUser, screenShare } = store.getState();
      const hasActiveScreenShare = screenShare.isSharing || Boolean(screenShare.sharingUserId);
      const isActuallySpeaking = store.getState().audioIndicator.activeSpeakers.includes(speakerId);
      if (
        ui.isAutoScreenSwitch
        && !hasActiveScreenShare
        && !mainUser.pinnedUserId
        && mainUser.pinnedUserIds.length === 0
        && mainUser.masterPinnedUserIds.length === 0
        && isActuallySpeaking
      ) {
        handlers.dispatch(mainUserActions.setMainUser(speakerId));
      }
    });

    // 채팅 + 음성 텍스트 (원본: MESSAGE_RECEIVED에서 JSON.parse 후 type 필드로 분기)
    room.on(E.MESSAGE_RECEIVED, (
      id: unknown,
      text: unknown,
      ts: unknown
    ) => {
      const senderId = String(id);
      const isLocalUser = senderId === room.myUserId();
      // 재접속 후 과거 jitsiId로 수신된 자신의 메시지 판별
      const isMyHistoricalMsg = !isLocalUser && this._isMyHistoricalJitsiId(senderId);
      const participant = (isLocalUser || isMyHistoricalMsg) ? null : room.getParticipantById(senderId);
      // senderName: displayName(이름) 우선, 없으면 닉네임, 없으면 캐시, 없으면 ID (기획: 참석자명 노출, 닉네임 제외)
      const senderDisplayName = (isLocalUser || isMyHistoricalMsg)
        ? ''
        : (store.getState().participants.participants[senderId]?.displayName
            || participant?.getDisplayName() || '');
      const senderNickname = (isLocalUser || isMyHistoricalMsg)
        ? (this._userInfo.nickname || '')
        : (store.getState().participants.participants[senderId]?.nickname
            ?? String(participant?.getProperty('nickname') ?? ''));
      const senderName = (isLocalUser || isMyHistoricalMsg)
        ? (this._userInfo.wehagoId || this._userInfo.nickname || senderId)
        : (senderDisplayName || senderNickname || this._getSenderNameFromCache(senderId) || senderId);
      const senderProfileUrl = (isLocalUser || isMyHistoricalMsg)
        ? toDisplayProfileUrl(this._userInfo.profileUrl, this._userInfo.avatarUrl) ?? ''
        : toDisplayProfileUrl(
            store.getState().participants.participants[senderId]?.profileUrl
              || String(participant?.getProperty('profile_url') ?? ''),
            store.getState().participants.participants[senderId]?.avatarUrl
              || String(participant?.getProperty('avatarUrl') ?? '')
          ) ?? '';
      // 타인의 이름을 캐시에 저장 (재접속 후 이름 조회용)
      if (!isLocalUser && !isMyHistoricalMsg && senderName && senderName !== senderId) {
        this._saveSenderNameToCache(senderId, senderName);
      }

      // 원본 ConferenceConnector.js: JSON.parse 후 type 필드가 있으면 voice message
      const rawText = String(text);
      let parsed: {
        type?: string;
        text?: string;
        voiceChatId?: string;
        isProgress?: string;
        isTranslated?: boolean;
        creditMode?: 'MASTER' | 'USER';
        message?: string;
        senderName?: string;
      } | null = null;
      try {
        const obj = JSON.parse(rawText);
        if (obj && typeof obj === 'object' && obj.type) {
          parsed = obj;
        }
      } catch {
        // JSON 파싱 실패 → 일반 채팅
      }

      if (parsed && parsed.type === 'system') {
        // ── 시스템 메시지 (START_STT, END_STT 등) → 대화기록에 record_line 표시 ──
        if (!isLocalUser && parsed.message) {
          const isSpeechRecognitionSystemMessage = parsed.message === SYSTEM_MSG.START_STT
            || parsed.message === SYSTEM_MSG.END_STT;
          if (isSpeechRecognitionSystemMessage && this._shouldUseJigasiSttResults()) return;

          const sysText = parsed.text || parsed.message;
          handlers.dispatch(captionActions.addEntry({
            id: `sys-${parsed.message}-${senderId}-${Date.now()}`,
            userId: senderId,
            userName: parsed.senderName || senderName,
            nickname: senderNickname,
            profileUrl: senderProfileUrl,
            text: sysText,
            timestamp: Date.now(),
            isFinal: true,
            isSystem: true,
            systemMessage: parsed.message,
          }));
        }
      } else if (parsed && parsed.type === 'voice') {
        // 음성 텍스트 메시지 → caption slice로 dispatch (원본 RECORD_MESSAGE_RECEIVED)
        if (!isLocalUser && parsed.text) {
          const shouldUseJigasiResults = this._shouldUseJigasiSttResults();
          if (parsed.isProgress === 'F') {
            // ── 확정된 텍스트 (번역 포함 가능) ──
            let displayText = parsed.text;
            let translatedText: string | null = null;

            let multiLangData: Record<string, string> | null = null;

            if (parsed.isTranslated) {
              // 다국어 JSON 파싱: { ko: "원문", en: "번역", origin: "ko" }
              try {
                multiLangData = JSON.parse(parsed.text) as Record<string, string>;
                const myLang = store.getState().translation.language;
                const originLang = multiLangData.origin || 'ko';

                // 원문 텍스트
                displayText = multiLangData[originLang] || parsed.text;

                // 내 언어 번역 결과
                if (myLang !== originLang && multiLangData[myLang]) {
                  translatedText = multiLangData[myLang];
                }
              } catch {
                // JSON 파싱 실패 → 원문 그대로 표시
                displayText = parsed.text;
                multiLangData = null;
              }
            }

            // Jigasi 모드: voiceChatId로 기존 원문 entry를 찾아 번역만 병합
            if (shouldUseJigasiResults && parsed.voiceChatId) {
              const existingEntry = store.getState().caption.entries.find(
                (e) => e.id === parsed!.voiceChatId,
              );
              if (existingEntry) {
                // 기존 entry에 번역 데이터 업데이트 (multiLangData 포함)
                handlers.dispatch(captionActions.updateEntryTranslation({
                  id: parsed.voiceChatId,
                  translatedText,
                  multiLangData,
                }));
                return;
              }
            }

            if (shouldUseJigasiResults) return;

            handlers.dispatch(captionActions.addEntry({
              id: `stt-${senderId}-${Date.now()}`,
              userId: senderId,
              userName: senderName,
              nickname: senderNickname,
              profileUrl: senderProfileUrl,
              text: displayText,
              translatedText,
              multiLangData,
              timestamp: Date.now(),
              isFinal: true,
            }));
            handlers.dispatch(captionActions.clearCurrentText(senderId));
          } else {
            if (shouldUseJigasiResults) return;
            // 진행 중인 텍스트 (interim)
            handlers.dispatch(captionActions.setCurrentText({
              speakerId: senderId,
              speakerName: senderName,
              nickname: senderNickname,
              profileUrl: senderProfileUrl,
              text: parsed.text,
            }));
          }
        }
      } else {
        // 일반 채팅 메시지 (현재 세션의 자신이 보낸 메시지는 sendChatMessage에서 이미 추가됨 → 스킵)
        if (isLocalUser) return;
        handlers.dispatch(
          chatActions.messageReceived({
            id: `msg-${senderId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            senderId,
            senderName,
            message: rawText,
            timestamp: new Date(String(ts)).getTime() || Date.now(),
            isPrivate: false,
            profileUrl: senderProfileUrl,
            isMine: isMyHistoricalMsg,
          })
        );

        // 채팅 패널을 보고 있지 않을 때만 토스트 알림 (재접속 시 재생된 과거 메시지 제외)
        const isChatVisible = store.getState().chat.isPanelOpen;
        if (!isChatVisible && !isMyHistoricalMsg) {
          showToast({ title: senderName, message: rawText });
        }
      }
    });

    room.on(E.PRIVATE_MESSAGE_RECEIVED, (
      id: unknown,
      text: unknown,
      ts: unknown
    ) => {
      const senderId = String(id);
      const participant = room.getParticipantById(senderId);
      const privateSenderName = participant?.getDisplayName() ?? senderId;
      const privateProfileUrl = toDisplayProfileUrl(
          store.getState().participants.participants[senderId]?.profileUrl
            || String(participant?.getProperty('profile_url') ?? ''),
          store.getState().participants.participants[senderId]?.avatarUrl
            || String(participant?.getProperty('avatarUrl') ?? '')
        ) ?? '';
      const privateText = String(text);
      handlers.dispatch(
        chatActions.messageReceived({
          id: `${senderId}-${ts}-private`,
          senderId,
          senderName: privateSenderName,
          message: privateText,
          timestamp: new Date(String(ts)).getTime() || Date.now(),
          isPrivate: true,
          profileUrl: privateProfileUrl,
        })
      );

      // 채팅 패널을 보고 있지 않을 때만 토스트 알림
      const isChatVisible = store.getState().chat.isPanelOpen;
      if (!isChatVisible) {
        showToast({ title: privateSenderName, message: privateText });
      }
    });

    // 녹화 상태 (공식 jitsi-meet middleware + V1 ConferenceConnector 패턴)
    room.on(E.RECORDER_STATE_CHANGED, (session: any) => {
      console.log('[Recording] RECORDER_STATE_CHANGED session:', session);

      const status = typeof session.getStatus === 'function' ? session.getStatus() : session._status;
      const sessionId = typeof session.getID === 'function' ? session.getID() : session._sessionID;
      const error = typeof session.getError === 'function' ? session.getError() : (session._error ?? null);
      const statusFromJicofo = session._statusFromJicofo;

      // 1. 에러가 있으면 에러 처리 (공식 jitsi-meet _showRecordingErrorNotification 패턴)
      // 여기서 return 하지 않는다 — 에러와 함께 오는 off 전이까지 아래에서 처리해야
      // 헤더의 '녹화 중' 표시가 남지 않는다 (Jibri가 녹화 중 죽으면 error + off가 같이 온다).
      if (error) {
        handlers.dispatch(recordingActions.setError(String(error)));
        const errorKey = `${sessionId}:${error}`;
        if (this._lastRecordingErrorKey !== errorKey) {
          this._lastRecordingErrorKey = errorKey;
          handlers.onRecordingError?.(String(error));
        }
      }

      // V1 방어 로직: _statusFromJicofo 가 'on'/'off' 면 우선 적용
      const finalStatus = (status === 'on' || statusFromJicofo === 'on') ? 'on' 
                        : (status === 'off' || statusFromJicofo === 'off') ? 'off' 
                        : status;

      // 2. 상태 업데이트
      handlers.dispatch(recordingActions.setStatus(finalStatus as 'idle' | 'pending' | 'on' | 'off'));

      // 새 참가자 입장 등으로 Jicofo가 presence를 재전송하면 상태값 변화 없이 이 이벤트가 다시 발생한다.
      // 실제로 상태가 바뀐 경우에만 스낵박스/콜백을 호출해 중복 알림을 막는다.
      const statusChanged = this._lastRecordingStatus !== finalStatus;
      this._lastRecordingStatus = finalStatus as 'idle' | 'pending' | 'on' | 'off';

      if (!statusChanged) {
        return;
      }

      if (finalStatus === 'on') {
        handlers.dispatch(
          recordingActions.setRecording({
            isRecording: true,
            sessionId: sessionId ?? undefined,
          })
        );
        handlers.onRecordingStateChanged?.(true, sessionId);
      } else if (finalStatus === 'off') {
        handlers.dispatch(
          recordingActions.setRecording({
            isRecording: false,
          })
        );
        handlers.onRecordingStateChanged?.(false, null);
      }
      // 'pending' 상태는 setStatus만 갱신 (UI에서 "녹화 준비 중..." 표시)
    });

    // 역할 변경
    room.on(E.USER_ROLE_CHANGED, (id: unknown, role: unknown) => {
      if (id === room.myUserId()) {
        handlers.dispatch(localActions.setModerator(String(role) === 'moderator'));
      } else {
        handlers.dispatch(
          participantsActions.participantUpdated({
            id: String(id),
            role: String(role),
            isModerator: String(role) === 'moderator',
          })
        );
      }
    });

    // 강퇴
    room.on(E.KICKED, () => {
      handlers.onConferenceFailed('kicked');
    });

    // 네트워크 품질 통계 (1초 쓰로틀)
    const statsThrottle: Map<string, number> = new Map();
    const STATS_THROTTLE_MS = 1000;

    room.on(events.connectionQuality.LOCAL_STATS_UPDATED, (stats: unknown) => {
      const s = stats as { connectionQuality?: number };
      const quality = s?.connectionQuality ?? 100;
      const now = Date.now();
      const last = statsThrottle.get('local') ?? 0;
      if (now - last > STATS_THROTTLE_MS) {
        statsThrottle.set('local', now);
        handlers.dispatch(localActions.setConnectionQuality(quality));
      }
    });

    room.on(events.connectionQuality.REMOTE_STATS_UPDATED, (id: unknown, stats: unknown) => {
      const participantId = String(id);
      const s = stats as { connectionQuality?: number };
      const quality = s?.connectionQuality ?? 100;
      const now = Date.now();
      const last = statsThrottle.get(participantId) ?? 0;
      if (now - last > STATS_THROTTLE_MS) {
        statsThrottle.set(participantId, now);
        handlers.dispatch(
          participantsActions.participantConnectionQualityUpdated({
            id: participantId,
            connectionQuality: quality,
          })
        );
      }
    });

    // ─── 화이트보드 메타데이터 (/video-renewal 전용) ─────────────────────────
    // v1: ConferenceConnector.js → JitsiConferenceEvents.METADATA_UPDATED
    //     → handlers.UPDATE_CONFERENCE_METADATA → VideoContainer.handleUpdateConferenceMetadata
    room.on(E.METADATA_UPDATED, (metadata: unknown) => {
      const md = metadata as Record<string, unknown>;
      const hasWhiteboardMetadata = Object.prototype.hasOwnProperty.call(md, METADATA_KEYS.WHITEBOARD_ID);
      const whiteboardData = md[METADATA_KEYS.WHITEBOARD_ID];
      const whiteboardOwner = md[METADATA_KEYS.WHITEBOARD_OWNER] as string | undefined;
      const rawFileMetadata = md[METADATA_KEYS.FILE_METADATA] as Record<string, unknown> | null | undefined;

      const isWhiteboardOpen = store.getState().participants.participantIds.includes(WHITEBOARD_ID);
      console.log('[DocShare:META] METADATA_UPDATED fired, whiteboardData:', !!whiteboardData, 'isWhiteboardOpen:', isWhiteboardOpen, 'docShare.isOpen:', store.getState().documentShare.isOpen);

      if (whiteboardData && !isWhiteboardOpen) {
        // 화이트보드 세션 시작 감지 (원격 발표자가 시작)
        // collabDetails 추출 (스케치: 객체, 문서공유: 배열)
        const isDocShare = Array.isArray(whiteboardData);

        // 소유자 이름 조회
        const ownerJitsiId = whiteboardOwner ?? '';
        const { participants: pState, local } = store.getState();
        let ownerName: string;
        if (ownerJitsiId === room.myUserId()) {
          ownerName = local.userName || local.nickname || '나';
        } else {
          const ownerP = pState.participants[ownerJitsiId];
          ownerName = ownerP?.displayName || ownerP?.nickname || '알 수 없음';
        }
        const shareLabel = isDocShare ? '문서 공유' : '스케치 공유';

        handlers.dispatch(participantsActions.addPseudoParticipant({
          id: WHITEBOARD_ID,
          name: `${ownerName}의 ${shareLabel}`,
          isWhiteboard: true,
          ownerId: ownerJitsiId,
        }));
        let collabDetails: { roomId: string; roomKey: string } | null = null;

        if (isDocShare && (whiteboardData as Array<{ pageNum: number; collabDetails: { roomId: string; roomKey: string } }>).length > 0) {
          collabDetails = (whiteboardData as Array<{ pageNum: number; collabDetails: { roomId: string; roomKey: string } }>)[0].collabDetails;
        } else if (!isDocShare && typeof whiteboardData === 'object') {
          collabDetails = whiteboardData as { roomId: string; roomKey: string };
        }

        if (!collabDetails) return;

        handlers.dispatch(whiteboardActions.openWhiteboard({
          ownerId: whiteboardOwner ?? '',
          isSketchMode: !isDocShare,
          collabDetails,
          isOwner: whiteboardOwner === room.myUserId(),
        }));
        // 화이트보드를 메인 유저로 핀 고정 + 사이드뷰로 전환
        handlers.dispatch(mainUserActions.pinUser(WHITEBOARD_ID));
        handlers.dispatch(uiActions.setScreenMode('SIDE'));

        if (isDocShare) {
          const collabDetailList = whiteboardData as Array<{ pageNum: number; collabDetails: { roomId: string; roomKey: string } }>;
          const totalPages = collabDetailList.length;

          handlers.dispatch(whiteboardActions.setCollabDetailList(collabDetailList));
          handlers.dispatch(whiteboardActions.setTotalPages(totalPages));

          // v1: FILE_METADATA는 { files: ImageData[], imageElements: ImageElement[] } 구조
          if (rawFileMetadata) {
            const files = rawFileMetadata.files as Array<{ mimeType: string; id: string; dataURL: string | null; created: number; thumbnailURL: string }> | null;
            if (files && Array.isArray(files)) {
              // 개발환경 URL 치환
              const normalizedFiles = files.map(f => ({
                ...f,
                thumbnailURL: f.thumbnailURL?.replace(
                  'http://dev.api0.wehago.com/',
                  'https://rtctest.wehago.com/api0/',
                ) ?? f.thumbnailURL,
              }));
              handlers.dispatch(whiteboardActions.setDocShareFiles(normalizedFiles));
            }
            // FileMetadata (UI 표시용) 생성
            handlers.dispatch(whiteboardActions.setFileMetadata({
              fileName: (rawFileMetadata.fileName as string) ?? '',
              totalPages,
              fileId: (rawFileMetadata.fileId as string) ?? `file-${Date.now()}`,
            }));
          }
        }
      } else if (hasWhiteboardMetadata && !whiteboardData && isWhiteboardOpen) {
        // 화이트보드 세션 종료 감지 (METADATA_UPDATED 경로)
        console.log('[DocShare:META] >>> whiteboard session END detected, cleaning up everything');
        const { mainUser } = store.getState();
        if (mainUser.pinnedUserId === WHITEBOARD_ID) {
          handlers.dispatch(mainUserActions.pinUser(null));
        }
        if (mainUser.mainUserId === WHITEBOARD_ID) {
          handlers.dispatch(mainUserActions.setMainUser('localUser'));
        }
        handlers.dispatch(whiteboardActions.reset());
        handlers.dispatch(participantsActions.removePseudoParticipant(WHITEBOARD_ID));
        // 문서공유/스케치 상태도 함께 정리 (SET_DOCUMENT_SHARE_IS_CLOSE 미수신 대비)
        handlers.dispatch(documentShareActions.reset());
        handlers.dispatch(drawingActions.reset());
        console.log('[DocShare:META] cleanup done, docShare.isOpen:', store.getState().documentShare.isOpen);
      } else if (whiteboardData && isWhiteboardOpen && Array.isArray(whiteboardData)) {
        // 이미 열린 문서공유 세션에서 FILE_METADATA 업데이트 감지
        // (setMetadata 순서 차이 또는 지연 이벤트 대응)
        const currentDocShareFiles = store.getState().whiteboard.docShareFiles;
        if (currentDocShareFiles.length === 0 && rawFileMetadata) {
          const files = rawFileMetadata.files as Array<{ mimeType: string; id: string; dataURL: string | null; created: number; thumbnailURL: string }> | null;
          if (files && Array.isArray(files) && files.length > 0) {
            const normalizedFiles = files.map(f => ({
              ...f,
              thumbnailURL: f.thumbnailURL?.replace(
                'http://dev.api0.wehago.com/',
                'https://rtctest.wehago.com/api0/',
              ) ?? f.thumbnailURL,
            }));
            handlers.dispatch(whiteboardActions.setDocShareFiles(normalizedFiles));
          }
          if (rawFileMetadata.fileName) {
            handlers.dispatch(whiteboardActions.setFileMetadata({
              fileName: (rawFileMetadata.fileName as string) ?? '',
              totalPages: (whiteboardData as Array<unknown>).length,
              fileId: (rawFileMetadata.fileId as string) ?? `file-${Date.now()}`,
            }));
          }
        }
      }
    });

    // ENDPOINT_MESSAGE_RECEIVED: 이모지 리액션 + Jigasi transcription-result
    room.on(E.ENDPOINT_MESSAGE_RECEIVED, (sender: unknown, eventData: unknown) => {
      const data = eventData as Record<string, unknown>;

      // ── 이모지 리액션 처리 ──
      if (data.name === CONFERENCE_COMMANDS.ENDPOINT_MEET_REACTION) {
        try {
          const emojiUserList = JSON.parse(data.emojiUserList as string);
          handlers.onEmojiReactionReceived?.(emojiUserList);
        } catch (e) {
          console.warn('[ConferenceConnector] Failed to parse emojiUserList:', e);
        }
      }

      // ── Jigasi transcription-result 처리 ──
      if (data.type === 'transcription-result') {
        if (!this._shouldUseJigasiSttResults()) {
          console.log('[ConferenceConnector] Jigasi result received before status ON; marking transcriber joined');
          this._markTranscriberJoined(handlers, null);
        }
        this._handleJigasiTranscriptionResult(data, room, handlers);
      }
    });

    // XMPP fallback: BridgeChannel이 준비되지 않았을 때 sendCommandOnce로 전송된 이모지 리액션 수신
    room.addCommandListener(CONFERENCE_COMMANDS.ENDPOINT_MEET_REACTION, (data: unknown) => {
      try {
        const cmd = data as { value?: string; attributes?: { emojiUserList?: string } };
        if (cmd.attributes?.emojiUserList) {
          const emojiUserList = JSON.parse(cmd.attributes.emojiUserList);
          handlers.onEmojiReactionReceived?.(emojiUserList);
        }
      } catch (e) {
        console.warn('[ConferenceConnector] Failed to parse XMPP fallback emojiUserList:', e);
      }
    });

    // END_MEETING_FOR_ALL: 마스터가 모든 참여자에게 회의 종료 알림
    room.addCommandListener(CONFERENCE_COMMANDS.END_MEETING_FOR_ALL, (...args: unknown[]) => {
      const data = args[0] as { value?: string; attributes?: { roomCreationTime?: string } } | undefined;
      const exitTime = Number(data?.value) || Date.now();
      const roomCreationTime = Number(data?.attributes?.roomCreationTime) || undefined;
      handlers.onEndMeetingForAll?.(exitTime, roomCreationTime);
    });

    // TRANSCRIPTION_STATUS_CHANGED: Jigasi transcriber 입/퇴장 감지
    // lib-jitsi-meet의 JitsiTranscriptionStatus enum값은 대문자('ON', 'OFF')
    room.on(E.TRANSCRIPTION_STATUS_CHANGED, (status: unknown, id: unknown) => {
      const s = String(status).toUpperCase();
      const jid = id ? String(id) : null;
      if (s === TRANSCRIBER_STATUS_ON) {
        console.log('[ConferenceConnector] Transcriber joined:', jid);
        this._markTranscriberJoined(handlers, jid);
      } else if (s === TRANSCRIBER_STATUS_OFF) {
        console.log('[ConferenceConnector] Transcriber left:', jid);
        this._markTranscriberLeft(handlers);

        // 서버 STT 전용 정책이므로 AI 번역 모드와 무관하게 transcriber를 재호출한다.
        console.log('[ConferenceConnector] Transcriber left, re-dialing...');
        this._startJigasiTranscription(handlers);
      }
    });
  }

  // ─── Jigasi Transcription ──────────────────────────────────────────────────

  private _startJigasiTranscription(handlers: ConferenceHandlers): void {
    const room = this._room;
    if (!room) return;

    // recorder(Jibri) 모드: STT/번역 제어(transcriber 초대·재호출)는 하지 않는다.
    // 이미 흐르고 있는 캡션/전사 결과 수신·렌더는 TRANSCRIPTION_STATUS_CHANGED 리스너와
    // 별도의 메시지 핸들러로 그대로 동작하므로 여기서 막아도 STT 오버레이 표시에는 영향 없다.
    if (isRecorderMode()) return;

    if (this._syncCurrentTranscriberState(handlers)) return;

    // 가드 1: 이미 transcriber가 ON 상태이면 skip
    // lib-jitsi-meet의 JitsiTranscriptionStatus enum은 대문자('ON', 'OFF')
    const status = this._getCurrentTranscriptionStatus();
    if (status && status !== TRANSCRIBER_STATUS_OFF) {
      console.log('[ConferenceConnector] Transcriber already active, skipping dial. status:', status);
      if (status === TRANSCRIBER_STATUS_ON) {
        this._markTranscriberJoined(handlers, null);
      } else {
        handlers.dispatch(captionActions.setTranscriberDialPending(true));
        this._scheduleTranscriberStatusSync(handlers);
      }
      return;
    }

    // 가드 2: 이미 dial() 호출 진행 중이면 skip (같은 클라이언트 race condition 방지)
    if (store.getState().caption.isTranscriberDialPending) {
      console.log('[ConferenceConnector] Transcriber dial already pending, skipping');
      this._scheduleTranscriberStatusSync(handlers);
      return;
    }

    handlers.dispatch(captionActions.setTranscriberDialPending(true));
    this._scheduleTranscriberStatusSync(handlers);

    room.dial('jitsi_meet_transcribe')
      .then(() => {
        console.log('[ConferenceConnector] Jigasi transcriber dial succeeded');
        this._scheduleTranscriberStatusSync(handlers);
      })
      .catch((e: unknown) => {
        console.error('[ConferenceConnector] Jigasi transcriber dial failed:', e);
        this._scheduleTranscriberStatusSync(handlers);
      });
  }

  /**
   * Jigasi 모드에서 자막 ON/OFF 또는 수신 언어 변경 시 참여자 속성을 갱신한다.
   * 외부(useSpeechRecognition, TranslationSettings 등)에서 호출.
   *
   * @param transcriptionRequested 자막/STT/번역 결과 수신 여부. false면 Jigasi에게 번역 수신 해제를 알린다.
   * @param language 수신 언어 코드('ko'|'en'|'ja' 등). transcriptionRequested=true일 때만 적용.
   */
  updateTranscriptionLanguage(transcriptionRequested: boolean, language: string): void {
    const room = this._room;
    if (!room) return;
    room.setLocalParticipantProperty('requestingTranscription', transcriptionRequested);
    room.setLocalParticipantProperty('translation_language', transcriptionRequested ? language : 'NONE');
    // vosktest gateway는 Jicofo participant details의 wehago.nationality를 번역 대상 언어로 사용한다.
    // 언어 변경 후에도 gateway target_langs가 갱신되도록 participant property를 같이 맞춘다.
    if (transcriptionRequested) {
      room.setLocalParticipantProperty('nationality', language);
      this._mergeWehagoIdentityAttributes({ nationality: language });
    }
  }

  /**
    * AI 번역 설정과 무관하게 Jigasi를 초대하고 현재 사용자의 결과 수신 속성을 갱신한다.
    * `isTranslationMode`는 기존 AI 번역 모드 메타데이터 의미를 유지한다.
    * 실제 STT 소스 선택은 transcriber 입장 상태(caption.isTranscriberJoined)를 기준으로 한다.
   */
  syncTranscriptionMode(transcriptionRequested: boolean, language: string): void {
    const room = this._room;
    if (!room) return;

    const isTranslationMode = this._isAiTranslationModeEnabled();
    room.setLocalParticipantProperty('isTranslationMode', isTranslationMode ? 'T' : 'F');
    this._mergeWehagoIdentityAttributes({ isTranslationMode: isTranslationMode ? 'T' : 'F' });

    this.updateTranscriptionLanguage(transcriptionRequested, language);
    if (this._handlers) {
      this._startJigasiTranscription(this._handlers);
    }
  }

  /**
   * Jigasi transcription-result 메시지를 대화기록 포맷으로 변환·적재한다.
   *
   * 서버 multiLang 프로토콜:
   * - text 필드가 항상 JSON: {"ko":"안녕하세요","origin":"ko"}
   * - 번역 포함 시: {"ko":"안녕하세요","en":"Hello","origin":"ko"}
   *
   * - 서버 번역 포함: multiLangData를 바로 반영, 클라이언트 번역/DB저장/브로드캐스트 생략
   * - 서버 번역 미포함 (하위 호환): 기존 translateOpenAI 로직 실행
   */
  private _handleJigasiTranscriptionResult(
    data: Record<string, unknown>,
    room: JitsiConference,
    handlers: ConferenceHandlers,
  ): void {
    const transcript = data.transcript as Array<{ text: string; confidence?: number }> | undefined;
    if (!transcript || transcript.length === 0) return;

    const rawText = transcript[0].text;
    if (!rawText) return;

    // ── 서버 multiLang 프로토콜 파싱 ──
    // 원문만: {"ko":"안녕하세요","origin":"ko"}
    // 번역 포함: {"ko":"안녕하세요","en":"Hello","origin":"ko"}
    let originalText = rawText;
    let multiLangData: Record<string, string> | null = null;
    let serverTranslated = false;

    try {
      const parsed = JSON.parse(rawText);
      if (parsed && typeof parsed === 'object' && parsed.origin) {
        multiLangData = parsed as Record<string, string>;
        originalText = parsed[parsed.origin] || rawText;
        // origin 키를 제외한 언어 키가 2개 이상이면 번역 포함
        const langKeys = Object.keys(parsed).filter((k) => k !== 'origin');
        serverTranslated = langKeys.length > 1;
      }
    } catch {
      // 하위 호환: 구 버전 서버의 일반 텍스트
    }

    const messageId = String(data.message_id ?? `jigasi-${Date.now()}`);
    const isInterim = Boolean(data.is_interim);
    const language = String(data.language ?? 'ko');
    const participant = data.participant as { id: string; name: string } | undefined;
    if (!participant?.id) return;

    const speakerId = participant.id;
    const speakerName = participant.name || speakerId;
    const isMine = speakerId === room.myUserId();

    // 닉네임 조회: Redux participants에서 가져오거나 participant.name 사용
    const participantState = store.getState().participants.participants[speakerId];
    const speakerNickname = isMine
      ? (this._userInfo.nickname || '')
      : (participantState?.nickname ?? speakerName);
    const speakerProfileUrl = isMine
      ? toDisplayProfileUrl(this._userInfo.profileUrl, this._userInfo.avatarUrl) ?? ''
      : toDisplayProfileUrl(
          participantState?.profileUrl
            || String(room.getParticipantById(speakerId)?.getProperty('profile_url') ?? ''),
          participantState?.avatarUrl
            || String(room.getParticipantById(speakerId)?.getProperty('avatarUrl') ?? '')
        ) ?? '';

    if (isInterim) {
      // ── 진행 중 텍스트 (interim) ──
      handlers.dispatch(captionActions.setCurrentText({
        speakerId,
        speakerName,
        nickname: speakerNickname,
        profileUrl: speakerProfileUrl,
        text: originalText,
      }));

      // 서버가 interim 번역을 포함한 경우 바로 반영 (미래 확장 대비)
      if (serverTranslated && multiLangData) {
        const myLang = store.getState().translation.language;
        const originLang = multiLangData.origin || 'ko';
        handlers.dispatch(captionActions.setCurrentTranslation({
          speakerId,
          translatedText: (myLang !== originLang && multiLangData[myLang]) ? multiLangData[myLang] : '',
          multiLangData,
        }));
      }
      // 서버 이관으로 클라이언트 부분 번역(JigasiPartialTranslator) 생략 → 비용 절감
      return;
    }

    // ── 확정 텍스트 (final) ──
    // 부분 번역기 리셋 (다음 발화 준비)
    if (this._partialTranslator) {
      this._partialTranslator.reset();
    }
    handlers.dispatch(captionActions.clearCurrentText(speakerId));

    // 내 언어 번역문 추출
    let translatedText: string | null = null;
    if (multiLangData) {
      const myLang = store.getState().translation.language;
      const originLang = multiLangData.origin || 'ko';
      if (myLang !== originLang && multiLangData[myLang]) {
        translatedText = multiLangData[myLang];
      }
    }

    handlers.dispatch(captionActions.upsertEntry({
      id: messageId,
      userId: speakerId,
      userName: speakerName,
      nickname: speakerNickname,
      profileUrl: speakerProfileUrl,
      text: originalText,
      nationality: multiLangData?.origin || language,
      timestamp: Date.now(),
      isFinal: true,
      translatedText,
      multiLangData,
    }));

    // ── 서버 번역 포함: 클라이언트 번역/DB저장/브로드캐스트 모두 생략 ──
    // 서버 Gateway가 번역 + DB 저장 + 크레딧 차감을 처리했으므로
    // 모든 참가자가 transcription-result로 multiLang을 동시에 수신함
    if (serverTranslated) return;

    // ── 하위 호환: 서버 번역 미포함 시 기존 클라이언트 번역 로직 ──
    // 내 메시지가 아니면 여기서 끝 (DB는 Jigasi가 처리)
    if (!isMine) return;

    // ── 내 메시지: 번역 필요 여부 판단 ──
    const translationState = store.getState().translation;
    const { translatingUsers } = translationState;
    const activeTranslators = Object.values(translatingUsers).filter((u) => u.enabled);
    if (activeTranslators.length === 0) return; // 번역 불필요 → Jigasi가 원문 DB 저장

    // Jigasi가 language를 "NONE"으로 보낼 수 있음 → 발화자 실제 언어로 fallback
    const fromLang = (!language || language === 'NONE')
      ? (store.getState().translation.language || 'ko')
      : language;
    const uniqueLangs = [...new Set(
      activeTranslators
        .map((u) => u.language)
        .filter((lang) => lang !== fromLang),
    )];
    if (uniqueLangs.length === 0) return; // 모든 번역 대상이 발화자와 동일 언어

    // ── 번역 필요: translateOpenAI 병렬 호출 ──
    const currentLocal = store.getState().local;
    const creditMode = currentLocal.creditMode;
    const roomId = currentLocal.roomId;

    Promise.all(
      uniqueLangs.map(async (toLang) => {
        try {
          const res = await translateOpenAI({
            input: originalText,
            fromLang,
            toLang,
            consumptionType: creditMode || 'MASTER',
            roomId: creditMode === 'MASTER' ? roomId : undefined,
          });

          if (res.resultMsg && res.resultMsg !== 'success') {
            if (res.resultMsg === '크레딧이 부족합니다.') {
              handlers.dispatch(translationActions.setTotalCredit(0));
              handlers.dispatch(translationActions.setTranslateEnabled(false));
            }
            return { lang: toLang, translated: null };
          }
          const translated = res.resultData?.translatedText;
          return { lang: toLang, translated: translated || null };
        } catch {
          return { lang: toLang, translated: null };
        }
      }),
    )
      .then((results) => {
        // 번역 실패(null)를 제외한 성공 결과만 필터링
        const successResults = results.filter(
          (r): r is { lang: TranslationLanguage; translated: string } => r.translated !== null,
        );
        if (successResults.length === 0) return; // 모든 번역 실패 → 원문만 Jigasi가 저장

        // 다국어 JSON 조립
        const multiLang: Record<string, string> = { [fromLang]: originalText, origin: fromLang };
        for (const r of successResults) {
          multiLang[r.lang] = r.translated;
        }
        const multiLangJson = JSON.stringify(multiLang);

        // Jitsi sendMessage: 기존 speechRecognition voice JSON 포맷과 동일
        const payload = {
          text: multiLangJson,
          type: 'voice',
          voiceChatId: messageId,
          isProgress: 'F',
          isTranslated: true,
          creditMode: creditMode || 'MASTER',
        };
        try {
          room.sendMessage(JSON.stringify(payload));
        } catch (err) {
          console.warn('[Jigasi] 번역 메시지 전송 실패:', err);
        }

        // 로컬 entry에 번역 + multiLangData 반영
        const myLang = store.getState().translation.language;
        handlers.dispatch(captionActions.updateEntryTranslation({
          id: messageId,
          translatedText: (myLang !== fromLang && multiLang[myLang]) ? multiLang[myLang] : null,
          multiLangData: multiLang,
        }));

        // DB 저장 (번역본 + 원문 포함)
        saveMessageToDB(multiLangJson, 'voice');
      })
      .catch((err) => {
        console.error('[Jigasi] 번역 처리 실패:', err);
      });
  }

  // ─── 룸 커맨드 수신 ───────────────────────────────────────────────────────

  private _bindCommandListeners(
    room: JitsiConference,
    handlers: ConferenceHandlers
  ): void {
    const dispatch = handlers.dispatch;

    // wehagoid: 닉네임/사용자 정보 공유 (참여자 입장 시 전송)
    room.addCommandListener(CONFERENCE_COMMANDS.WEHAGO_ID, (values: unknown) => {
      const v = values as { value?: string; attributes?: Record<string, string> };
      const id = v?.value ?? '';
      const attrs = v?.attributes ?? {};
      if (!id) return;

      // 기본 필드
      const nickname = attrs.nickname ?? '';
      const wehagoId = attrs.wehagoId ?? '';
      const avatarUrl = attrs.avatarUrl ?? '';
      const selectedAvatar = attrs.selectedAvatar ?? '';
      const nationality = attrs.nationality ?? '';
      const companyCode = attrs.companyCode ?? '';

      // v1 호환 필드
      const userIdentificationCode = attrs.userIdentificationCode ?? '';
      const profileUrl = toAbsoluteProfileUrl(attrs.profile_url) ?? '';
      const companyFullpath = attrs.companyFullpath ?? '';
      const isExternalParticipant = attrs.isExternalParticipant === 'true';
      const userName = attrs.userName ?? '';
      const avatar = attrs.avatar ?? '';
      const externalUserId = attrs.externalUserId ?? '';
      const userContact = attrs.user_contact ?? '';
      const userEmail = attrs.user_email ?? '';
      const isScreen = attrs.is_screen === 'true';
      const userNo = Number(attrs.user_no) || 0;
      const userType = attrs.user_type ?? '';
      const host = attrs.host === 'true';
      const isTranslationMode = attrs.isTranslationMode ?? '';
      const enableAutoLang = attrs.enableAutoLang ?? 'false';
      const voiceNoteLocale = attrs.voiceNoteLocale ?? '';
      const employeeNo = attrs.employee_no ?? '';
      const companyNo = attrs.company_no ?? '';

      // 닉네임 및 추가 정보 업데이트
      dispatch(participantsActions.participantUpdated({
        id,
        ...(nickname && { nickname }),
        ...(wehagoId && { wehagoId }),
        ...(avatarUrl && { avatarUrl }),
        ...(selectedAvatar && { selectedAvatar }),
        ...(nationality && { nationality }),
        ...(companyCode && { companyCode }),
        // v1 호환 필드
        userIdentificationCode,
        ...(profileUrl && { profileUrl }),
        companyFullpath,
        isExternalParticipant,
        userName,
        avatar,
        externalUserId,
        userContact,
        userEmail,
        isScreen,
        userNo,
        userType,
        host,
        isTranslationMode,
        enableAutoLang,
        voiceNoteLocale,
        employeeNo,
        companyNo,
      }));
    });

    // ─── 발언권 ─────────────────────────────────────────────────────────

    const myUserId = room.myUserId();

    // 발언권 요청 수신 (마스터만 처리)
    room.addCommandListener(CONFERENCE_COMMANDS.REQUEST_FLOOR, (values: unknown) => {
      const v = values as { value?: string; attributes?: { targetUser?: string } };
      let userId = v?.value ?? '';
      // v1 호환: targetUser JSON format
      if (!userId && v?.attributes?.targetUser) {
        try {
          const parsed = JSON.parse(v.attributes.targetUser);
          userId = parsed.id || parsed.jitsiId || '';
        } catch { /* ignore */ }
      }
      if (userId) {
        dispatch(floorActions.addRequest(userId));
      }
    });

    // 발언권 전체 허가/거부
    room.addCommandListener(CONFERENCE_COMMANDS.GRANT_FLOOR, (values: unknown) => {
      const v = values as { value?: string; attributes?: { targetUser?: string; type?: string } };
      const type = v?.attributes?.type ?? 'accept';
      let targetUsers: string[] = [];

      // targetUser JSON 파싱 (v1 호환: 단일 또는 배열)
      if (v?.attributes?.targetUser) {
        try {
          const parsed = JSON.parse(v.attributes.targetUser);
          targetUsers = Array.isArray(parsed)
            ? parsed.map((u: { id?: string; jitsiId?: string }) => u.id || u.jitsiId || '')
            : [parsed.id || parsed.jitsiId || ''];
        } catch { /* ignore */ }
      } else if (v?.value) {
        targetUsers = [v.value];
      }

      for (const userId of targetUsers) {
        if (!userId) continue;
        dispatch(floorActions.removeRequest(userId));
        if (type === 'accept') {
          dispatch(floorActions.addGrant(userId));
        }
        // 로컬 사용자 발언권 상태 업데이트
        if (userId === myUserId) {
          if (type === 'accept') {
            dispatch(floorActions.setGranted(true));
            dispatch(floorActions.setRequesting(false));
          } else {
            dispatch(floorActions.setGranted(false));
            dispatch(floorActions.setRequesting(false));
          }
        }
      }
    });

    // 발언권 특정 사용자 허가/거부
    room.addCommandListener(CONFERENCE_COMMANDS.GRANT_FLOOR_TARGET, (values: unknown) => {
      const v = values as { attributes?: { targetUser?: string; type?: string; isMasterControlTarget?: string } };
      const type = v?.attributes?.type ?? 'accept';
      let userId = '';

      if (v?.attributes?.targetUser) {
        try {
          const parsed = JSON.parse(v.attributes.targetUser);
          userId = parsed.id || parsed.jitsiId || '';
        } catch { /* ignore */ }
      }
      if (!userId) return;

      dispatch(floorActions.removeRequest(userId));
      if (type === 'accept') {
        dispatch(floorActions.addGrant(userId));
      }

      // 로컬 사용자 발언권 상태
      if (userId === myUserId) {
        if (type === 'accept') {
          dispatch(floorActions.setGranted(true));
          dispatch(floorActions.setRequesting(false));
        } else {
          dispatch(floorActions.setGranted(false));
          dispatch(floorActions.setRequesting(false));
        }
      }
    });

    // 발언권 종료
    room.addCommandListener(CONFERENCE_COMMANDS.STOP_FLOOR, (values: unknown) => {
      const v = values as { value?: string; attributes?: { targetUser?: string } };
      let userId = v?.value ?? '';

      if (!userId && v?.attributes?.targetUser) {
        try {
          const parsed = JSON.parse(v.attributes.targetUser);
          userId = parsed.id || parsed.jitsiId || '';
        } catch { /* ignore */ }
      }
      if (!userId) return;

      dispatch(floorActions.removeGrant(userId));
      // 로컬 사용자 발언권 종료
      if (userId === myUserId) {
        dispatch(floorActions.setGranted(false));
        dispatch(floorActions.setRequesting(false));
      }
    });

    // 화면 공유 상태
    room.addCommandListener(CONFERENCE_COMMANDS.IS_DESKTOP_SHARED, (values: unknown) => {
      const v = values as { attributes?: { userId?: string; isShared?: string } };
      const userId = v?.attributes?.userId ?? '';
      const isShared = v?.attributes?.isShared === 'true';

      // 늦게 입장한 참가자에게 Jitsi가 과거 커맨드(start)를 재전달할 수 있음.
      // 이 경우 실제 데스크톱 트랙이 muted 또는 disposed 상태이면 무시해야 함.
      let effectiveIsShared = isShared;
      if (isShared && userId && userId !== room.myUserId()) {
        const participant = room.getParticipantById(userId);
        if (participant) {
          const remoteTracks = participant.getTracks?.() ?? [];
          const desktopTrack = remoteTracks.find((t: any) => t.getType?.() === 'video' && t.videoType === 'desktop');
          if (!desktopTrack || desktopTrack.disposed || (typeof desktopTrack.isMuted === 'function' && desktopTrack.isMuted())) {
            effectiveIsShared = false;
          }
        }
      }

      dispatch(screenShareActions.setSharingUserId(effectiveIsShared ? userId : null));

      // 원격 참여자의 isScreenSharing Redux 상태도 동기화
      if (userId && userId !== room.myUserId()) {
        dispatch(participantsActions.participantTrackUpdated({
          id: userId,
          tracks: { isScreenSharing: effectiveIsShared },
        }));
      }

      // 원격 참여자가 화면 공유를 시작하면 해당 참여자를 핀 고정 + SIDE 모드 전환
      // 단, 이미 핀이 설정되어 있으면(사용자가 직접 선택했거나 다른 공유자를 보고 있으면) 자동 전환하지 않음
      if (effectiveIsShared && userId && userId !== room.myUserId()) {
        const { mainUser: mu } = store.getState();
        if (!mu.pinnedUserId) {
          dispatch(mainUserActions.pinUser(userId));
          dispatch(uiActions.setScreenMode('SIDE'));
        }
      }
      // 화면 공유 종료 시 핀 해제
      if (!effectiveIsShared && userId) {
        const currentPinned = store.getState().mainUser.pinnedUserId;
        if (currentPinned === userId) {
          dispatch(mainUserActions.pinUser(null));
        }
      }
    });

    // 번역 모드
    room.addCommandListener(CONFERENCE_COMMANDS.IS_TRANSLATION_MODE, (values: unknown) => {
      const v = values as { attributes?: { isEnabled?: string } };
      const isEnabled = v?.attributes?.isEnabled === 'true';
      dispatch(voiceNoteActions.setTranslationMode(isEnabled));
    });

    // 번역 토글 상태 수신 — 참여자가 번역 ON/OFF 했을 때 translatingUsers 갱신
    room.addCommandListener(CONFERENCE_COMMANDS.TRANSLATION_TOGGLE_STATUS, (values: unknown) => {
      const v = values as { attributes?: { userId?: string; enabled?: string; language?: string } };
      const userId = v?.attributes?.userId ?? '';
      const enabled = v?.attributes?.enabled === 'true';
      const language = (v?.attributes?.language ?? 'ko') as TranslationLanguage;
      if (userId) {
        dispatch(translationActions.setUserTranslationStatus({ userId, enabled, language }));
      }
    });

    // 음성인식(STT) 버튼 상태 — 다른 참여자가 STT ON/OFF 했을 때 추적
    room.addCommandListener(CONFERENCE_COMMANDS.CHANGE_SPEECH_BUTTON, (values: unknown) => {
      const v = values as { value?: string; attributes?: { isSpeechStart?: string } };
      const userId = String(v?.value ?? '');
      const enabled = v?.attributes?.isSpeechStart === 'true';
      if (userId) {
        dispatch(voiceNoteActions.setSttParticipant({ userId, enabled }));
      }
    });

    // 고스트 유저 정리 (중복 접속 처리)
    room.addCommandListener(CONFERENCE_COMMANDS.CLEAR_GHOST_USER, (values: unknown) => {
      const v = values as { attributes?: { wehagoId?: string } };
      const wehagoId = v?.attributes?.wehagoId ?? '';
      // 동일 wehagoId의 이전 세션 강제 퇴장 처리
      if (wehagoId) {
        const participants = room.getParticipants();
        const ghost = participants.find(
          (p) => p.getProperty('wehagoId') === wehagoId
        );
        if (ghost && ghost.getId() !== room.myUserId()) {
          room.kickParticipant(ghost.getId());
        }
      }
    });

    // 동일 참여자 삭제 (SET_DELETE_SAME_PARTICIPANT)
    room.addCommandListener(CONFERENCE_COMMANDS.SET_DELETE_SAME_PARTICIPANT, (values: unknown) => {
      const v = values as { attributes?: { participantId?: string } };
      const participantId = v?.attributes?.participantId ?? '';
      
      // 전달받은 타겟 ID가 나라면 (내가 쫓겨나야 할 이전 세션이라면)
      if (participantId === room.myUserId()) {
        handlers.onConferenceFailed('duplicate_login');
      }
    });

    // 참여자 비디오 뮤트 강제 변경
    room.addCommandListener(
      CONFERENCE_COMMANDS.CHANGE_PARTICIPANT_MUTE_VIDEO,
      (values: unknown) => {
        const v = values as {
          attributes?: { targetUserId?: string; muted?: string };
        };
        const targetId = v?.attributes?.targetUserId ?? '';
        const muted = v?.attributes?.muted === 'true';
        if (targetId === room.myUserId()) {
          // 나에 대한 명령 → 비디오 뮤트 처리는 VideoPage에서
          console.log('[ConferenceConnector] force video mute:', muted);
        }
      }
    );

    // STT 마이크 권한 목록
    room.addCommandListener(
      CONFERENCE_COMMANDS.SET_VOICE_NOTE_MIC_GRANT_LIST,
      (values: unknown) => {
        const v = values as { value?: string };
        try {
          const list = JSON.parse(v?.value ?? '[]') as string[];
          dispatch(voiceNoteActions.setMicGrantList(list));
        } catch {
          // ignore
        }
      }
    );

    // 스케치 모드 시작/종료 (원본: SET_DRAWING_IS_SHARE)
    room.addCommandListener(CONFERENCE_COMMANDS.SET_DRAWING_IS_SHARE, (values: unknown) => {
      const v = values as {
        value?: string; // Jitsi userId
        attributes?: { isDrawingShare?: string };
      };
      const senderId = v?.value ?? '';
      if (senderId === room.myUserId()) return; // 자신이 보낸 커맨드 무시

      const isDrawingShare = v?.attributes?.isDrawingShare === 'true';
      if (isDrawingShare) {
        dispatch(drawingActions.setIsSketchShare(true));
        dispatch(drawingActions.setIsSketchOwner(false));
        dispatch(drawingActions.setSketchOwner(senderId));
      } else {
        dispatch(drawingActions.setIsSketchShare(false));
        dispatch(drawingActions.setIsSketchOwner(false));
        dispatch(drawingActions.setSketchOwner(''));
      }
    });

    // 스케치 모드 데이터 동기화 (원본: UPDATE_DOCUMENT_DATA, canvasType:'sketch')
    room.addCommandListener(CONFERENCE_COMMANDS.UPDATE_DOCUMENT_DATA, (values: unknown) => {
      const v = values as {
        value?: string; // 발표자 userId
        attributes?: {
          canvasType?: string;
          documentData?: string;
          from?: string;
          sketchWidthLimit?: string;
          sketchHeightLimit?: string;
        };
      };
      const canvasType = v?.attributes?.canvasType;
      if (canvasType !== 'sketch') return;

      const ownerId = v?.value ?? '';
      const documentDataStr = v?.attributes?.documentData;
      console.log(`[Drawing] UPDATE_DOCUMENT_DATA RECVD. canvasType=${canvasType}, ownerId=${ownerId}, dataLen=${documentDataStr?.length}`);

      // 발표자 자신이 보낸 데이터는 무시 (원본 DrawingContainer 패턴)
      // ownerId는 Jitsi ID — room.myUserId()와 비교 (원본과 동일)
      // 주의: WEHAGO ID로 비교하면 같은 계정의 다른 탭에서도 무시됨
      if (ownerId === room.myUserId()) return;

      const from = v?.attributes?.from ?? 'web';
      const widthLimit = Number(v?.attributes?.sketchWidthLimit ?? 1920);
      const heightLimit = Number(v?.attributes?.sketchHeightLimit ?? 1080);

      // 스케치 상태(isSketchShare)는 SET_DRAWING_IS_SHARE에서만 관리
      // 여기서는 데이터만 업데이트 (isSketchShare가 false면 무시)
      const currentDrawing = store.getState().drawing;
      if (!currentDrawing.reduxIsSketchShare) return;

      dispatch(drawingActions.setSketchOwner(ownerId));
      dispatch(drawingActions.setSketchFrom(from));
      dispatch(drawingActions.setSketchLimit({ width: widthLimit, height: heightLimit }));

      if (documentDataStr) {
        try {
          const parsed = JSON.parse(documentDataStr);
          dispatch(drawingActions.setSketchData(parsed));
        } catch {
          // ignore parse error
        }
      }
    });

    // 문서 공유 시작 (오너→참여자)
    // 원본 wehago-conference 포맷: value = Jitsi userId, attributes = { owner, resources (JSON), fileName }
    room.addCommandListener(
      CONFERENCE_COMMANDS.SET_DOCUMENT_SHARE_IS_OPEN,
      (values: unknown) => {
        const v = values as {
          value?: string;
          attributes?: { owner?: string; resources?: string; fileName?: string };
        };
        const ownerId = v?.value ?? '';
        const fileName = v?.attributes?.fileName ?? '';
        let resources: string[] = [];
        try {
          resources = JSON.parse(v?.attributes?.resources ?? '[]');
        } catch { /* ignore */ }
        const myId = room.myUserId();
        dispatch(
          documentShareActions.setOpen({
            isOpen: true,
            resources,
            fileName,
            ownerId,
            isOwner: ownerId === myId,
          })
        );
      }
    );

    // 문서 공유 종료
    room.addCommandListener(CONFERENCE_COMMANDS.SET_DOCUMENT_SHARE_IS_CLOSE, () => {
      console.log('[DocShare:CMD] SET_DOCUMENT_SHARE_IS_CLOSE received! docShare.isOpen was:', store.getState().documentShare.isOpen);
      dispatch(documentShareActions.reset());
      dispatch(drawingActions.reset());
      // 화이트보드 세션도 정리 (메타데이터는 오너가 제거하므로 수신 측은 Redux만 정리)
      const { mainUser } = store.getState();
      if (mainUser.mainUserId === WHITEBOARD_ID) {
        dispatch(mainUserActions.setMainUser('localUser'));
      }
      dispatch(whiteboardActions.reset());
      dispatch(participantsActions.removePseudoParticipant(WHITEBOARD_ID));
    });

    // 문서 공유 페이지 변경 (원본: SET_DOCUMENT_PAGE)
    room.addCommandListener(CONFERENCE_COMMANDS.SET_DOCUMENT_PAGE, (values: unknown) => {
      const v = values as {
        value?: string;
        attributes?: { page?: string };
      };
      const ownerId = v?.value ?? '';
      if (ownerId === room.myUserId()) return; // 자신이 보낸 커맨드 무시
      const page = Number(v?.attributes?.page ?? 0);
      dispatch(documentShareActions.setSelectResource(page));
    });

    // 문서 공유 드로잉 데이터 수신 (UPDATE_DOCUMENT_DATA에서 canvasType !== 'sketch' 분기)
    // 위의 UPDATE_DOCUMENT_DATA 리스너에서 canvasType='sketch'만 처리하므로,
    // canvasType이 없거나 'document'인 경우를 별도로 처리
    room.addCommandListener(CONFERENCE_COMMANDS.UPDATE_DOCUMENT_DATA, (values: unknown) => {
      const v = values as {
        value?: string;
        attributes?: {
          canvasType?: string;
          documentData?: string;
          selectResource?: string;
          dataURL?: string;
          from?: string;
        };
      };
      const canvasType = v?.attributes?.canvasType;
      // sketch는 위 리스너에서 처리 — 여기서는 document 전용
      if (canvasType === 'sketch') return;

      const ownerId = v?.value ?? '';
      if (ownerId === room.myUserId()) return;

      const documentDataStr = v?.attributes?.documentData;
      const pageIndex = Number(v?.attributes?.selectResource ?? 0);
      if (documentDataStr) {
        try {
          const parsed = JSON.parse(documentDataStr);
          // 해당 페이지의 objectData에 저장 (비발표자도 페이지별 드로잉 유지)
          dispatch(documentShareActions.setPageObjectData({
            pageIndex,
            object: parsed,
            dataURL: null,
          }));
          // 현재 보고 있는 페이지와 같으면 즉시 렌더링을 위해 receivedDrawingData도 갱신
          const currentPage = store.getState().documentShare.selectResource;
          if (pageIndex === currentPage) {
            dispatch(documentShareActions.setReceivedDrawingData(parsed));
          }
        } catch { /* ignore */ }
      }
    });

    // 스케치 캔버스 클리어 (원본: CLEAR_DRAWING_CANVAS)
    room.addCommandListener(CONFERENCE_COMMANDS.CLEAR_DRAWING_CANVAS, (values: unknown) => {
      const v = values as { value?: string };
      const senderId = v?.value ?? '';
      if (senderId === room.myUserId()) return;
      dispatch(drawingActions.clearSketchData());
    });

    // 문서공유 캔버스 클리어 (원본: CLEAR_DOCUMENT_CANVAS)
    room.addCommandListener(CONFERENCE_COMMANDS.CLEAR_DOCUMENT_CANVAS, (values: unknown) => {
      const v = values as { value?: string };
      const senderId = v?.value ?? '';
      if (senderId === room.myUserId()) return;
      const currentPage = store.getState().documentShare.selectResource;
      dispatch(documentShareActions.setPageObjectData({ pageIndex: currentPage, object: null, dataURL: null }));
      dispatch(documentShareActions.setReceivedDrawingData(null));
    });

    // 문서공유 특정 타겟 — 늦은 참가자용 (원본: DOCUMENT_SHARE_TARGET)
    room.addCommandListener(CONFERENCE_COMMANDS.DOCUMENT_SHARE_TARGET, (values: unknown) => {
      const v = values as {
        value?: string;
        attributes?: {
          target?: string;
          resources?: string;
          owner?: string;
          fileName?: string;
          selectResource?: string;
          objectData?: string;
        };
      };
      const target = v?.attributes?.target ?? '';
      if (target !== room.myUserId()) return; // 나에게 보낸 것만 처리

      const ownerId = v?.value ?? '';
      let resources: string[] = [];
      try { resources = JSON.parse(v?.attributes?.resources ?? '[]'); } catch { /* ignore */ }
      const fileName = v?.attributes?.fileName ?? '';
      const selectResource = Number(v?.attributes?.selectResource ?? 0);

      dispatch(documentShareActions.setOpen({
        isOpen: true,
        resources,
        fileName,
        ownerId,
        isOwner: false,
      }));
      dispatch(documentShareActions.setIsModerate(true));
      dispatch(documentShareActions.setSelectResource(selectResource));
    });

    // 드로잉 특정 타겟 — 늦은 참가자용 (원본: DRAWING_SHARE_TARGET)
    room.addCommandListener(CONFERENCE_COMMANDS.DRAWING_SHARE_TARGET, (values: unknown) => {
      const v = values as {
        value?: string;
        attributes?: {
          target?: string;
          objectData?: string;
          from?: string;
        };
      };
      const target = v?.attributes?.target ?? '';
      if (target !== room.myUserId()) return;

      const ownerId = v?.value ?? '';
      const from = v?.attributes?.from ?? 'web';

      dispatch(drawingActions.setIsSketchShare(true));
      dispatch(drawingActions.setIsSketchOwner(false));
      dispatch(drawingActions.setSketchOwner(ownerId));
      dispatch(drawingActions.setSketchFrom(from));

      const objectDataStr = v?.attributes?.objectData;
      if (objectDataStr) {
        try {
          const parsed = JSON.parse(objectDataStr);
          dispatch(drawingActions.setSketchData(parsed));
        } catch { /* ignore */ }
      }
    });

    // ── 마스터/호스트 관련 커맨드 (VideoPage에서 API 호출) ──────────────────

    // 마스터 유저 목록 변경 → getMasterUsers(token)
    room.addCommandListener(CONFERENCE_COMMANDS.CHANGE_MASTER_USER_LIST, () => {
      handlers.onMasterListChanged?.();
    });

    // 남은 마스터 조회 → getRemainingMaster(roomId)
    room.addCommandListener(CONFERENCE_COMMANDS.GET_REMAINING_MASTER, () => {
      handlers.onRemainingMasterRequested?.();
    });

    // 방 공개여부 초기화 → getRoomIsPublic(roomId)
    room.addCommandListener(CONFERENCE_COMMANDS.INIT_ROOM_PUBLIC_MODE, () => {
      handlers.onRoomPublicModeInit?.();
    });

    // 마스터 마이크 제어 → updateMasterControlUser(token, {audioActive})
    room.addCommandListener(CONFERENCE_COMMANDS.CHANGE_MASTER_MIC_CONTROL, (values: unknown) => {
      const v = values as { attributes?: { controlType?: string } };
      const controlType = v?.attributes?.controlType ?? '';
      handlers.onMasterMicControlChanged?.(controlType);
    });

    // 마스터 비디오 순서 변경 → updateMasterControlUser(token, {videoseq})
    room.addCommandListener(CONFERENCE_COMMANDS.CHANGE_MASTER_VIDEO_SEQ, (values: unknown) => {
      const v = values as { attributes?: { videoseq?: string } };
      const videoseq = v?.attributes?.videoseq ?? '';
      handlers.onMasterVideoSeqChanged?.(videoseq);
    });

    // 마스터 목록 동기화 (토큰 있는 유저가 브로드캐스트 → 토큰 없는 비회원도 수신)
    room.addCommandListener(CONFERENCE_COMMANDS.SYNC_MASTER_USERS, (values: unknown) => {
      const v = values as {
        value?: string;
        attributes?: {
          action?: string;
          senderId?: string;
          target?: string;
          userName?: string;
        };
      };
      try {
        const list = JSON.parse(v?.value || '[]') as MasterUser[];
        if (Array.isArray(list)) {
          const current = store.getState().master.masterUsers;
          // 내용이 다르면 업데이트한다. 빈 배열도 마스터 해제 동기화에 필요하다.
          if (JSON.stringify(current) !== JSON.stringify(list)) {
            // 로컬 유저가 마스터 권한을 잃게 되는 경우, 모바일 presence의
            // 오래된 SYNC_MASTER_USERS 데이터일 수 있으므로 API로 재검증한다.
            const localWehagoId = store.getState().local.wehagoId;
            const wasLocalMaster = !!localWehagoId && current.some((m) => m.user === localWehagoId);
            const isStillMaster = !!localWehagoId && list.some((m) => m.user === localWehagoId);
            if (wasLocalMaster && !isStillMaster) {
              handlers.onMasterListChanged?.();
            } else {
              handlers.dispatch(masterActions.setMasterUsers(list));
            }
          }
        }

        const action = v?.attributes?.action as 'grant' | 'clear' | undefined;
        const target = v?.attributes?.target ?? '';
        const senderId = v?.attributes?.senderId ?? '';
        const userName = v?.attributes?.userName ?? '';
        if (
          (action === 'grant' || action === 'clear')
          && target === room.myUserId()
          && senderId !== room.myUserId()
        ) {
          handlers.onMasterPermissionChanged?.(action, userName);
        }
      } catch { /* JSON 파싱 실패 무시 */ }
    });

    // 마스터 화면 고정 브로드캐스트 (v1 UPDATE_MASTER_PIN_FIXED)
    room.addCommandListener(CONFERENCE_COMMANDS.UPDATE_MASTER_PIN_FIXED, (values: unknown) => {
      const v = values as { value?: string; attributes?: { masterPinnedUsers?: string; userName?: string; type?: string; isScreenShare?: string; actionTimestamp?: string } };
      // 자신이 보낸 에코 무시 — 로컬 상태는 이미 dispatch로 업데이트됨
      if (v?.value === room.myUserId()) return;
      try {
        const masterPinnedUserIds: string[] = JSON.parse(v?.attributes?.masterPinnedUsers || '[]');
        const userName = v?.attributes?.userName ?? '';
        const type = (v?.attributes?.type ?? 'pin') as 'pin' | 'unpin';
        const isScreenShare = v?.attributes?.isScreenShare === 'true';
        const actionTimestamp = v?.attributes?.actionTimestamp ? parseInt(v.attributes.actionTimestamp, 10) : undefined;
        handlers.onMasterPinFixed?.(masterPinnedUserIds, userName, type, isScreenShare, actionTimestamp);
      } catch { /* JSON 파싱 실패 무시 */ }
    });

    // 방생성자(최초 마스터) 화면 상태 구독 — recorder(Jibri) 전용.
    room.addCommandListener(CONFERENCE_COMMANDS.SYNC_CREATOR_SCREEN, (values: unknown) => {
      if (!isRecorderMode()) return;
      const v = values as { value?: string };
      try {
        const state = JSON.parse(v?.value || '{}') as {
          screenMode?: 'GRID' | 'SIDE' | 'PRESENTER';
          isShareClick?: boolean;
          presenterOverrideUserId?: string | null;
          gridPage?: number;
          mainUserId?: string | null;
          pinnedUserId?: string | null;
          pinnedUserIds?: string[];
        };
        if (state.pinnedUserIds) {
          handlers.dispatch(mainUserActions.setPinnedUsers(state.pinnedUserIds));
        }
        if (typeof state.isShareClick === 'boolean') {
          handlers.dispatch(uiActions.setIsShareClick(state.isShareClick));
        }
        handlers.dispatch(uiActions.setPresenterOverrideUserId(state.presenterOverrideUserId ?? null));
        // setPinnedUsers/setMasterPinnedUsers의 부수효과로 mainUserId가 갱신될 수 있으므로,
        // 방생성자의 최종 mainUserId를 마지막에 다시 덮어써 정확히 일치시킨다.
        handlers.dispatch(mainUserActions.setMainUser(state.mainUserId ?? null));
        if (state.screenMode) {
          handlers.dispatch(uiActions.setScreenMode(state.screenMode));
        }
        // GRID 페이지 — 방생성자가 2페이지 이후를 보고 있으면 recorder도 같은 페이지를 그린다.
        if (typeof state.gridPage === 'number') {
          handlers.dispatch(uiActions.setGridPage(state.gridPage));
        }
      } catch { /* JSON 파싱 실패 무시 */ }
    });

    // 마스터 → 참여자 미디어 제어 (모두 끄기 / 켜기 요청 / 개별)
    room.addCommandListener(CONFERENCE_COMMANDS.MASTER_MEDIA_CONTROL, (values: unknown) => {
      const v = values as {
        value?: string;
        attributes?: { action?: string; mediaType?: string; target?: string };
      };
      // 자신이 보낸 에코 무시
      if (v?.value === room.myUserId()) return;
      const action = v?.attributes?.action as 'mute' | 'request_unmute' | undefined;
      const mediaType = v?.attributes?.mediaType as 'audio' | 'video' | undefined;
      const target = v?.attributes?.target ?? '';
      if (!action || !mediaType) return;
      // target 필터: 'all'이면 모두 수신, 아니면 해당 유저만
      if (target !== 'all' && target !== room.myUserId()) return;
      handlers.onMasterMediaControl?.(action, mediaType);
    });

    // ── 녹화 관련 커맨드 (VideoPage에서 API 호출) ───────────────────────────

    // 녹화 요청 → recordRequest(roomId, userId)
    room.addCommandListener(CONFERENCE_COMMANDS.REQUEST_RECORD_USER, (values: unknown) => {
      const v = values as { value?: string };
      const userId = v?.value ?? '';
      if (userId) handlers.onRecordRequested?.(userId);
    });

    // 녹화 데이터 수정 → updateRecordData(roomId, data)
    room.addCommandListener(CONFERENCE_COMMANDS.UPDATE_RECORD_DATA, (values: unknown) => {
      const v = values as { attributes?: Record<string, unknown> };
      const data = v?.attributes ?? {};
      handlers.onUpdateRecordData?.(data);
    });

    // 회의방 정보 변경 알림 (마스터가 회의 설정을 수정한 경우)
    // → 모든 참여자가 입장 시와 동일한 getVideoRoomInfo 재조회로 로컬 상태 갱신
    room.addCommandListener(CONFERENCE_COMMANDS.ROOM_INFO_UPDATED, (values: unknown) => {
      const v = values as { value?: string; attributes?: { senderId?: string; accessType?: string } };
      // 본인이 보낸 커맨드는 무시 (송신 측에서 이미 갱신함)
      if (v?.attributes?.senderId && v.attributes.senderId === room.myUserId()) return;

      // 커맨드에 포함된 accessType (v1 API의 is_all_access가 반영되지 않을 수 있으므로 우선 사용)
      const commandAccessType = v?.attributes?.accessType;

      void (async () => {
        try {
          // 동적 import로 순환 의존 회피
          const { default: apiManager } = await import('@/api');
          const roomInfo = await apiManager.getVideoRoomInfo(this._roomId);
          const info = roomInfo as Record<string, unknown>;

          dispatch(localActions.mergeRoomInfo({
            roomName: (info.name as string) ?? this._roomId,
            isPublic: Boolean(info.is_public),
            securityType: (info.security_type === 'MASTER_APPROVAL' || info.security_type === 'PASSWORD')
              ? info.security_type as 'MASTER_APPROVAL' | 'PASSWORD'
              : '',
            joinCode: String(info.join_code ?? ''),
            linkedMeeting: info.linked_room
              ? {
                  roomId: String(info.linked_room),
                  name: String(info.linked_room_name ?? ''),
                  startDateTime: (info.linked_room_start_time as number) ?? null,
                  endDateTime: (info.linked_room_end_time as number) ?? null,
                  accessType: commandAccessType === 'PREVIOUS_ONLY' ? 'PREVIOUS_ONLY'
                    : commandAccessType === 'ALL' ? 'ALL'
                    : (info.is_all_access ? 'ALL' : 'PREVIOUS_ONLY'),
                }
              : null,
            aiTranslationType: (info.ai_translation_type as 'USE_CREATOR_CREDIT' | 'USE_PARTICIPANT_CREDIT' | null) ?? null,
          }));
        } catch (err) {
          console.warn('[ConferenceConnector] ROOM_INFO_UPDATED 재조회 실패:', err);
        }
      })();
    });

    // 초대된 참석자 목록 변경 알림
    // → 수신 측은 invitedParticipantVersion을 증가시켜 ParticipantList에서 재조회
    room.addCommandListener(CONFERENCE_COMMANDS.INVITE_LIST_UPDATED, (values: unknown) => {
      const v = values as { value?: string };
      // 자신이 보낸 커맨드는 무시 (송신 측에서 이미 bump함)
      const senderId = v?.value ?? '';
      if (senderId === String(room.myUserId())) return;
      dispatch(uiActions.bumpInvitedParticipantVersion());
    });
  }

  // ─── 이벤트 핸들러 ────────────────────────────────────────────────────────

  private _handleRemoteTrackAdded(
    track: JitsiRemoteTrack,
    handlers: ConferenceHandlers
  ): void {
    const participantId = track.getParticipantId();

    // Jigasi(Transcriber) 등 hidden 참여자의 트랙은 무시
    if (this._hiddenParticipantIds.has(participantId)) {
      return;
    }

    const mediaType = track.getType();
    const isMuted = track.isMuted();
    const videoType = (track as any).videoType as string | undefined;
    const isDesktop = mediaType === 'video' && videoType === 'desktop';

    // Redux: 트랙 상태 업데이트
    handlers.dispatch(
      participantsActions.participantTrackUpdated({
        id: participantId,
        tracks: {
          ...(mediaType === 'audio'
            ? { hasAudio: true, audioMuted: isMuted }
            : isDesktop
              // 이미 muted된 데스크톱 트랙은 종료된 화면공유의 잔여 트랙이므로 isScreenSharing을 true로 설정하지 않음
              ? { isScreenSharing: !isMuted }
              : { hasVideo: true, videoMuted: isMuted }),
        },
      })
    );

    // 비디오 트랙의 sourceName 저장 (quality 조절에 사용)
    if (mediaType === 'video') {
      const sourceName = track.getSourceName();
      if (sourceName) {
        if (isDesktop) {
          this._remoteScreenSourceNames.set(participantId, sourceName);
        } else {
          this._remoteVideoSourceNames.set(participantId, sourceName);
        }

        // sourceName 등록 후 현재 정책으로 receiver quality 재적용
        // (USER_JOINED 시점에는 sourceName이 없어 constraint 미반영)
        console.log('%c[RemoteTrackAdded] sourceName registered, re-applying quality', 'color:#f0f;font-weight:bold', participantId, sourceName, isDesktop ? 'screen' : 'camera');
        this._reapplyCurrentReceiverQuality();
      }
    }

    // TrackContext: 실제 트랙 객체 전달
    this.onRemoteTrackAdded?.(participantId, track);
  }

  private _handleRemoteTrackRemoved(
    track: JitsiRemoteTrack,
    handlers: ConferenceHandlers
  ): void {
    const participantId = track.getParticipantId();

    // Jigasi(Transcriber) 등 hidden 참여자의 트랙은 무시
    if (this._hiddenParticipantIds.has(participantId)) {
      return;
    }
    const mediaType = track.getType();
    const videoType = (track as any).videoType as string | undefined;
    // getVideoType()도 사용 가능하면 참조 (disposed 전이므로 접근 가능)
    const videoTypeAlt = typeof (track as any).getVideoType === 'function'
      ? (track as any).getVideoType() as string | undefined
      : undefined;
    const resolvedVideoType = videoType || videoTypeAlt;
    const isDesktop = mediaType === 'video' && resolvedVideoType === 'desktop';

    handlers.dispatch(
      participantsActions.participantTrackUpdated({
        id: participantId,
        tracks: {
          ...(mediaType === 'audio'
            ? { hasAudio: false }
            : isDesktop
              ? { isScreenSharing: false }
              : { hasVideo: false }),
        },
      })
    );

    if (mediaType === 'video') {
      if (isDesktop) {
        this._remoteScreenSourceNames.delete(participantId);
      } else {
        this._remoteVideoSourceNames.delete(participantId);
      }
      this._reapplyCurrentReceiverQuality();
    }

    // TrackContext: 트랙 제거
    this.onRemoteTrackRemoved?.(participantId, track);
  }

  private _handleTrackMuteChanged(
    track: JitsiLocalTrack | JitsiRemoteTrack,
    handlers: ConferenceHandlers
  ): void {
    if (track.disposed) return;
    const isMuted = track.isMuted();
    const mediaType = track.getType();
    const videoTypeAlt = typeof (track as any).getVideoType === 'function' ? (track as any).getVideoType() : undefined;
    const videoType = (track as any).videoType || videoTypeAlt;
    const isDesktop = mediaType === 'video' && videoType === 'desktop';

    if (track.isLocal()) {
      if (mediaType === 'audio') {
        handlers.onLocalAudioMuteChanged(isMuted);
        // 음소거 시 activeSpeakers에서 즉시 제거 (발언 아이콘 잔류 방지)
        const localJitsiId = this._room?.myUserId();
        if (isMuted && localJitsiId) {
          handlers.dispatch(audioIndicatorActions.setActiveSpeaker({ userId: localJitsiId, isSpeaking: false }));
        }
      } else if (mediaType === 'video' && !isDesktop) {
        handlers.onLocalVideoMuteChanged(isMuted);
      }
    } else {
      const remoteTrack = track as JitsiRemoteTrack;
      if (isDesktop) return; // 화면 공유 트랙의 mute 이벤트가 카메라의 videoMuted를 덮어쓰지 않도록 무시
      const remoteId = remoteTrack.getParticipantId();
      // Jigasi(Transcriber) 등 hidden 참여자의 트랙은 무시
      if (this._hiddenParticipantIds.has(remoteId)) return;
      handlers.dispatch(
        participantsActions.participantTrackUpdated({
          id: remoteId,
          tracks: {
            ...(mediaType === 'audio'
              ? { audioMuted: isMuted }
              : { videoMuted: isMuted }),
          },
        })
      );
      // 원격 참여자 음소거 시 activeSpeakers에서 즉시 제거
      if (mediaType === 'audio' && isMuted) {
        handlers.dispatch(audioIndicatorActions.setActiveSpeaker({ userId: remoteId, isSpeaking: false }));
      }
    }
  }

  private _handleAudioLevel(
    userId: string,
    audioLevel: number,
    handlers: ConferenceHandlers
  ): void {
    // 쓰로틀: AUDIO_LEVEL_THROTTLE_MS 이내의 중복 dispatch 방지
    const now = Date.now();
    const last = this._lastAudioLevelDispatch.get(userId) ?? 0;
    if (now - last < AUDIO_LEVEL_THROTTLE_MS) return;

    this._lastAudioLevelDispatch.set(userId, now);

    // 로컬 사용자 뮤트 상태면 발언으로 처리하지 않음
    const isLocalUser = userId === this._room?.myUserId();
    const isLocalMuted = isLocalUser && this._localAudioTrack && !this._localAudioTrack.disposed && this._localAudioTrack.isMuted();

    // 발언 중 감지: audioLevel > 0.02 이면 발언 중으로 판단 (뮤트 상태면 무조건 false)
    const isSpeaking = !isLocalMuted && audioLevel > 0.02;
    handlers.dispatch(audioIndicatorActions.setActiveSpeaker({ userId, isSpeaking }));

    // audioLevel은 Redux 외부 스토어로 관리 (activeSpeaker 디스패치 이후 호출)
    audioLevelStore.setLevel(userId, audioLevel);
  }

  private _handleUserJoined(
    id: string,
    participant: JitsiParticipant,
    handlers: ConferenceHandlers
  ): void {
    const statsId = (participant as JitsiParticipant & { getStatsID?: () => string }).getStatsID?.();
    const isJibri = statsId === 'wehagorecord' || statsId === 'wehagorecord-dev';

    if (this._isJigasiTranscriberParticipant(participant) || isJibri) {
      this._hiddenParticipantIds.add(id);
      handlers.dispatch(participantsActions.participantLeft(id));
      if (!isJibri) {
        this._markTranscriberJoined(handlers, id);
      }
      return;
    }

    // Jigasi(Transcriber) 등 hidden 참여자는 UI에 표시하지 않음
    if (participant.isHidden()) {
      this._hiddenParticipantIds.add(id);
      // 트랙 이벤트로 미리 생성된 플레이스홀더가 있으면 제거
      handlers.dispatch(participantsActions.participantLeft(id));
      return;
    }

    // ── 늦게 전달된 중복 접속자(이전 세션) 강제 퇴장 처리 ──
    const room = this._room;
    if (room) {
      const localUserProp = room.getLocalParticipantProperty('user');
      const remoteUserProp = participant.getProperty('user');
      if (localUserProp && localUserProp === remoteUserProp) {
        const isLocalBlock = room.getLocalParticipantProperty('blockSimultaneousAccess') !== 'false';
        const isRemoteBlock = participant.getProperty('blockSimultaneousAccess') !== 'false';
        
        if (isLocalBlock && isRemoteBlock) {
          const localJoinedAt = Number(room.getLocalParticipantProperty('joinedAt') || 0);
          const remoteJoinedAt = Number(participant.getProperty('joinedAt') || 0);
          
          // 내가 더 늦게 접속한 새 세션이라면, 뒤늦게 이벤트로 들어온 이전 세션을 쫓아낸다
          if (localJoinedAt > remoteJoinedAt) {
            this.sendCommand(CONFERENCE_COMMANDS.SET_DELETE_SAME_PARTICIPANT, {
              value: room.myUserId(),
              attributes: {
                participantId: id,
              },
            });
            return; // UI에 추가하지 않고 무시
          }
        }
      }
    }

    handlers.dispatch(
      participantsActions.participantJoined({
        id,
        displayName: participant.getDisplayName() ?? '',
        nickname: String(participant.getProperty('nickname') ?? ''),
        wehagoId: String(participant.getProperty('wehagoId') ?? ''),
        companyCode: String(participant.getProperty('companyCode') ?? ''),
        avatarUrl: String(participant.getProperty('avatarUrl') ?? ''),
        selectedAvatar: String(participant.getProperty('selectedAvatar') ?? ''),
        nationality: String(participant.getProperty('nationality') ?? ''),
        role: participant.getRole(),
        isModerator: participant.isModerator(),
        isHidden: participant.isHidden(),
        connectionStatus: 'active',
        connectionQuality: 100,
        // v1 호환 필드 (wehagoid 커맨드 수신 시 갱신됨)
        userIdentificationCode: '',
        profileUrl: '',
        companyFullpath: '',
        isExternalParticipant: false,
        userName: '',
        avatar: '',
        externalUserId: '',
        userContact: '',
        userEmail: '',
        isScreen: false,
        userNo: 0,
        userType: '',
        host: false,
        isTranslationMode: '',
        enableAutoLang: String(participant.getProperty('enableAutoLang') ?? 'false'),
        voiceNoteLocale: '',
        employeeNo: '',
        companyNo: '',
      })
    );

    // ── 늦은 참가자에게 현재 스케치/문서공유 상태 전송 ──
    if (!room) return;
    const state = store.getState();

    // 문서공유 중이고 내가 발표자면 → DOCUMENT_SHARE_TARGET
    if (state.documentShare.isOpen && state.documentShare.isOwner) {
      const ds = state.documentShare;
      room.sendCommandOnce(CONFERENCE_COMMANDS.DOCUMENT_SHARE_TARGET, {
        value: room.myUserId(),
        attributes: {
          target: id,
          resources: JSON.stringify(ds.resources),
          owner: state.local.userId,
          fileName: ds.fileName,
          selectResource: String(ds.selectResource),
          objectData: JSON.stringify(ds.objectData),
        },
      });
    }

    // 스케치 중이고 내가 발표자면 → DRAWING_SHARE_TARGET
    if (state.drawing.reduxIsSketchShare && state.drawing.reduxIsSketchOwner) {
      room.sendCommandOnce(CONFERENCE_COMMANDS.DRAWING_SHARE_TARGET, {
        value: room.myUserId(),
        attributes: {
          target: id,
          objectData: JSON.stringify(state.drawing.reduxSketchData ?? {}),
          from: 'web',
        },
      });
    }

    // ── ENTER_ROOM: 참여자 입장 DB 적재 (노출 없음, 로그성) ──
    const joinedParticipant = participant;
    saveMessageToDB(SYSTEM_MSG.ENTER_ROOM, 'system', {
      userName: joinedParticipant.getDisplayName() ?? '',
      jitsiId: id,
      nickname: String(joinedParticipant.getProperty('nickname') ?? ''),
      portalId: String(joinedParticipant.getProperty('wehagoId') ?? ''),
      profileUrl: String(joinedParticipant.getProperty('profile_url') ?? ''),
    });
  }

  private _handleUserLeft(
    id: string,
    handlers: ConferenceHandlers
  ): void {
    const state = store.getState();
    const wasTranscriber = state.caption.transcriberJID === id || this._transcriberParticipantIds.has(id);
    this._hiddenParticipantIds.delete(id);
    this._transcriberParticipantIds.delete(id);
    handlers.dispatch(participantsActions.participantLeft(id));
    audioLevelStore.clearLevel(id);
    handlers.dispatch(audioIndicatorActions.setActiveSpeaker({ userId: id, isSpeaking: false }));

    // 퇴장한 유저 핀 상태 정리
    handlers.dispatch(mainUserActions.removeUser(id));

    if (state.screenShare.sharingUserId === id) {
      handlers.dispatch(screenShareActions.setSharingUserId(null));
    }

    this._remoteVideoSourceNames.delete(id);
    this._remoteScreenSourceNames.delete(id);
    this._reapplyCurrentReceiverQuality();

    // 나간 유저의 번역 상태 정리
    handlers.dispatch(translationActions.removeTranslatingUser(id));
    // 나간 유저의 STT 상태 정리
    handlers.dispatch(voiceNoteActions.removeSttParticipant(id));

    if (wasTranscriber) {
      this._markTranscriberLeft(handlers);
      this._startJigasiTranscription(handlers);
    }

    // 나간 유저가 스케치/문서공유 오너인 경우 → 상태 정리 (v1 USER_LEFT_CHECK_DOCUMENT 패턴)
    if (state.drawing.reduxIsSketchShare && state.drawing.reduxSketchOwner === id) {
      handlers.dispatch(drawingActions.reset());
    }
    if (state.documentShare.isOpen && !state.documentShare.isOwner) {
      // 원격 문서공유 오너가 나간 경우 문서공유 종료
      const docOwnerId = state.documentShare.ownerId;
      if (docOwnerId === id) {
        handlers.dispatch(documentShareActions.reset());
      }
    }

    // 화이트보드(Excalidraw) 오너가 나간 경우 → 세션 강제 종료
    // stopWhiteboardSession()이 메타데이터 제거 + Redux 상태 초기화 + pseudo participant 제거를 모두 처리
    if (state.whiteboard.isOpen && state.whiteboard.ownerId === id) {
      console.log('[Whiteboard] Owner left (%s), force-stopping whiteboard session', id);
      stopWhiteboardSession();
    }

    // ── OUT_ROOM: 참여자 퇴장 DB 적재 (노출 없음, 로그성) ──
    const leftParticipant = state.participants.participants[id];
    saveMessageToDB(SYSTEM_MSG.OUT_ROOM, 'system', {
      userName: leftParticipant?.displayName ?? '',
      jitsiId: id,
      nickname: leftParticipant?.nickname ?? '',
      portalId: leftParticipant?.wehagoId ?? '',
      profileUrl: leftParticipant?.profileUrl ?? '',
    });

    // TrackContext: 참여자 트랙 제거
    this.onParticipantLeft?.(id);
  }

  // ─── 트랙 교체 (디바이스 변경, 화면 공유) ────────────────────────────────

  async replaceAudioTrack(
    newDeviceId: string,
    handlers: ConferenceHandlers
  ): Promise<void> {
    const oldTrack = this._localAudioTrack;
    const shouldKeepMuted = oldTrack && !oldTrack.disposed
      ? oldTrack.isMuted()
      : store.getState().local.audioMuted;
    const audioCreateOptions: { devices: ['audio']; micDeviceId?: string } = { devices: ['audio'] };
    if (newDeviceId) {
      audioCreateOptions.micDeviceId = newDeviceId;
    }
    const [newTrack] = await window.JitsiMeetJS.createLocalTracks(audioCreateOptions);

    if (!newTrack) return;

    if (shouldKeepMuted) {
      await newTrack.mute();
    }

    try {
      if (oldTrack && !oldTrack.disposed) {
        await this._room?.replaceTrack(oldTrack, newTrack);
      } else {
        await this._room?.addTrack(newTrack);
      }
    } catch (err) {
      await safeDisposeTrack(newTrack);
      throw err;
    }

    if (oldTrack && !oldTrack.disposed) {
      oldTrack.removeAllListeners(
        window.JitsiMeetJS.events.track.TRACK_AUDIO_LEVEL_CHANGED
      );
      await safeDisposeTrack(oldTrack);
    }

    this._localAudioTrack = newTrack;
    handlers.onLocalAudioTrackAdded(newTrack);

    // 새 로컬 오디오 트랙에 오디오 레벨 리스너 등록
    newTrack.on(
      window.JitsiMeetJS.events.track.TRACK_AUDIO_LEVEL_CHANGED,
      (audioLevel: number) => {
        if (newTrack.isMuted()) return;
        const localJitsiId = this._room?.myUserId();
        if (localJitsiId) {
          this._handleAudioLevel(localJitsiId, audioLevel, handlers);
        }
      }
    );

    const appliedDeviceId = newDeviceId || newTrack.getDeviceId?.() || '';
    handlers.dispatch(localActions.setAudioInputDeviceId(appliedDeviceId));

    // 디바이스 교체 후 노이즈 제거 효과 재적용
    if (this._noiseSuppressionEnabled) {
      await this._applyNoiseSuppression(newTrack);
    }
  }

  async replaceVideoTrack(
    newDeviceId: string,
    handlers: ConferenceHandlers,
    options?: { unmute?: boolean }
  ): Promise<void> {
    const oldTrack = this._localVideoTrack;
    const shouldKeepMuted = options?.unmute
      ? false
      : oldTrack && !oldTrack.disposed
        ? oldTrack.isMuted()
        : store.getState().local.videoMuted;
    const videoCreateOptions: {
      devices: ['video'];
      cameraDeviceId?: string;
      resolution: number;
      constraints: typeof config.constraints;
    } = {
      devices: ['video'],
      resolution: 720,
      constraints: config.constraints,
    };
    if (newDeviceId) {
      videoCreateOptions.cameraDeviceId = newDeviceId;
    }
    const [newTrack] = await window.JitsiMeetJS.createLocalTracks(videoCreateOptions);

    if (!newTrack) return;

    if (shouldKeepMuted) {
      await newTrack.mute();
    }

    try {
      if (oldTrack && !oldTrack.disposed) {
        await this._room?.replaceTrack(oldTrack, newTrack);
      } else {
        await this._room?.addTrack(newTrack);
      }
    } catch (err) {
      await safeDisposeTrack(newTrack);
      throw err;
    }

    if (oldTrack && !oldTrack.disposed) {
      await safeDisposeTrack(oldTrack);
    }

    this._localVideoTrack = newTrack;
    handlers.onLocalVideoTrackAdded(newTrack);
    const appliedDeviceId = newDeviceId || newTrack.getDeviceId?.() || '';
    handlers.dispatch(localActions.setVideoInputDeviceId(appliedDeviceId));
  }

  // ─── 오디오/비디오 뮤트 ──────────────────────────────────────────────────

  async audioMute(): Promise<void> {
    if (this._localAudioTrack && !this._localAudioTrack.disposed) {
      await this._localAudioTrack.mute();
    }
  }

  async audioUnmute(): Promise<void> {
    if (this._localAudioTrack && !this._localAudioTrack.disposed) {
      await this._localAudioTrack.unmute();
    }
  }

  // ─── 노이즈 제거 ──────────────────────────────────────────────────────────

  /**
   * 노이즈 제거 효과를 오디오 트랙에 적용한다.
   * lib-jitsi-meet의 track.setEffect()를 사용.
   */
  private async _applyNoiseSuppression(track: JitsiLocalTrack): Promise<void> {
    if (!track || track.disposed) {
      console.warn('[RNNoise] _applyNoiseSuppression: 트랙 없음 또는 disposed');
      return;
    }
    try {
      const effect = new NoiseSuppressionEffect();
      console.log('[RNNoise] 오디오 트랙에 setEffect() 호출 시작...');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (track as any).setEffect(effect);
      console.log('[RNNoise] ✅ setEffect() 완료 — 노이즈 제거 활성화됨');
    } catch (err) {
      console.error('[RNNoise] ❌ setEffect() 실패:', err);
    }
  }

  /**
   * 노이즈 제거 활성/비활성 전환.
   */
  async setNoiseSuppressionEnabled(enabled: boolean): Promise<void> {
    console.log(`[RNNoise] setNoiseSuppressionEnabled(${enabled})`);
    this._noiseSuppressionEnabled = enabled;

    if (!this._localAudioTrack || this._localAudioTrack.disposed) {
      console.warn('[RNNoise] 오디오 트랙 없음 — 상태만 저장');
      return;
    }

    if (enabled) {
      await this._applyNoiseSuppression(this._localAudioTrack);
    } else {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (this._localAudioTrack as any).setEffect(undefined);
        console.log('[RNNoise] 노이즈 제거 비활성화됨');
      } catch (err) {
        console.error('[RNNoise] 노이즈 제거 해제 실패:', err);
      }
    }
  }

  isNoiseSuppressionEnabled(): boolean {
    return this._noiseSuppressionEnabled;
  }

  async videoMute(): Promise<void> {
    if (this._localVideoTrack && !this._localVideoTrack.disposed) {
      await this._localVideoTrack.mute();
    }
  }

  async videoUnmute(): Promise<void> {
    if (this._localVideoTrack && !this._localVideoTrack.disposed) {
      await this._localVideoTrack.unmute();
    } else if (this._room && this._handlers) {
      // 카메라 OFF 상태로 입장한 경우 트랙이 없으므로 새로 생성하여 conference에 추가
      const deviceId = store.getState().local.videoInputDeviceId || '';
      await this.replaceVideoTrack(deviceId, this._handlers, { unmute: true });
    }
  }

  // ─── 화면 공유 ────────────────────────────────────────────────────────────

  /**
   * 화면 공유 시작 — addTrack 방식 (멀티스트림)
   *
   * 카메라(v0)는 그대로 유지하고 데스크탑(v1)을 별도의 비디오 트랙으로 추가한다.
   * JitsiConference.addTrack()은 videoType이 다른 비디오 트랙의 동시 존재를 허용한다.
   * (JitsiConference.js line 1122: `!localTracks.find(t => t.getVideoType() === track.getVideoType())`)
   */
  async startScreenShare(handlers: ConferenceHandlers): Promise<void> {
    console.log('[SS] startScreenShare called');
    if (!window.JitsiMeetJS.isDesktopSharingEnabled()) {
      console.error('[SS] Desktop sharing not enabled');
      throw new Error('Desktop sharing is not available');
    }

    let screenTracks: JitsiLocalTrack[];
    try {
      console.log('[SS] calling createLocalTracks desktop...');
      screenTracks = await window.JitsiMeetJS.createLocalTracks({
        devices: ['desktop'],
        desktopSharingFrameRate: config.desktopSharingFrameRate,
      });
      console.log('[SS] createLocalTracks OK, count=', screenTracks.length, screenTracks.map((t: JitsiLocalTrack) => `${t.getType()}/${t.getVideoType()}`));
    } catch (err) {
      const errName = (err as { name?: string })?.name ?? '';
      console.error('[SS] createLocalTracks error:', errName, (err as Error).message);
      if (errName === 'gum.screensharing_user_canceled') {
        return;
      }
      throw err;
    }

    // createLocalTracks가 데스크탑 오디오 트랙을 함께 반환할 수 있으므로 비디오 트랙을 명시적으로 선택
    const screenTrack = screenTracks.find(t => t.getType() === 'video') ?? null;
    console.log('[SS] screenTrack:', screenTrack ? `${screenTrack.getType()}/${screenTrack.getVideoType()}` : 'null');

    // 사용하지 않는 트랙(데스크탑 오디오 등) 정리
    for (const t of screenTracks) {
      if (t !== screenTrack) safeDisposeTrack(t);
    }

    if (!screenTrack) return;

    // addTrack으로 데스크탑 트랙을 별도의 비디오 소스로 추가 (카메라 유지)
    try {
      console.log('[SS] adding desktop track via addTrack (multi-stream)...');
      await this._room?.addTrack(screenTrack);
      console.log('[SS] desktop track added OK');
    } catch (err) {
      console.error('[SS] addTrack error:', (err as Error).message, err);
      await safeDisposeTrack(screenTrack);
      throw err;
    }

    this._localScreenTrack = screenTrack;
    handlers.dispatch(screenShareActions.setSharing(true));

    // 로컬 사용자도 SIDE 모드로 전환하여 내 공유 화면 프리뷰 표시
    const localJitsiId = this._room?.myUserId();
    if (localJitsiId) {
      handlers.dispatch(mainUserActions.pinUser(localJitsiId));
    }
    handlers.dispatch(uiActions.setScreenMode('SIDE'));

    // TrackContext에 화면 공유 트랙 전달
    handlers.onLocalScreenShareTrackAdded?.(screenTrack);

    // 브라우저 기본 "공유 중지" 버튼 클릭 시 자동으로 stopScreenShare 호출
    screenTrack.on(
      window.JitsiMeetJS.events.track.LOCAL_TRACK_STOPPED,
      () => {
        if (this._localScreenTrack === screenTrack) {
          this.stopScreenShare(handlers).catch(console.warn);
        }
      }
    );

    // 화면 공유 알림 커맨드 전송
    this._room?.sendCommand(CONFERENCE_COMMANDS.IS_DESKTOP_SHARED, {
      attributes: {
        userId: this._room.myUserId(),
        isShared: 'true',
      },
    });
  }

  /**
   * 화면 공유 종료 — removeTrack 방식 (멀티스트림)
   *
   * 데스크탑 트랙만 제거하고 카메라는 그대로 유지한다.
   * conference.removeTrack()은 내부적으로 replaceTrack(track, null)을 호출하여
   * source-remove IQ를 원격 참여자에게 전송한다.
   */
  async stopScreenShare(handlers: ConferenceHandlers): Promise<void> {
    const screenTrack = this._localScreenTrack;
    if (!screenTrack || screenTrack.disposed) return;

    // 데스크탑 트랙만 회의에서 제거 (카메라 트랙은 영향 없음)
    try {
      await this._room?.removeTrack(screenTrack);
    } catch (err) {
      console.error('[SS] removeTrack error:', (err as Error).message);
    }

    // TrackContext에서 화면 공유 트랙 제거
    handlers.onLocalScreenShareTrackRemoved?.(screenTrack);

    await safeDisposeTrack(screenTrack);
    this._localScreenTrack = null;

    handlers.dispatch(screenShareActions.setSharing(false));
    handlers.dispatch(screenShareActions.setSharingUserId(null));

    // 로컬 핀 해제 — jitsiId 기준으로 비교
    const localJitsiId = this._room?.myUserId();
    const currentPinned = store.getState().mainUser.pinnedUserId;
    if (currentPinned === localJitsiId) {
      handlers.dispatch(mainUserActions.pinUser(null));
    }

    // 화면 공유 종료 알림
    this._room?.sendCommand(CONFERENCE_COMMANDS.IS_DESKTOP_SHARED, {
      attributes: {
        userId: this._room.myUserId(),
        isShared: 'false',
      },
    });
  }

  // ─── 참여자 제어 ─────────────────────────────────────────────────────────

  kickParticipant(userId: string): void {
    this._room?.kickParticipant(userId);
  }

  muteRemoteAudio(userId: string): void {
    const room = this._room;
    if (!room) return;
    room.muteParticipant(userId, 'audio' as never);
    // 수신측 스낵바 표시를 위해 커맨드도 전송
    room.sendCommandOnce(CONFERENCE_COMMANDS.MASTER_MEDIA_CONTROL, {
      value: room.myUserId(),
      attributes: { action: 'mute', mediaType: 'audio', target: userId },
    });
  }

  unmuteRemoteAudio(userId: string): void {
    const room = this._room;
    if (!room) return;
    room.sendCommandOnce(CONFERENCE_COMMANDS.MASTER_MEDIA_CONTROL, {
      value: room.myUserId(),
      attributes: { action: 'unmute', mediaType: 'audio', target: userId },
    });
  }

  /**
   * 마스터 → 특정 참여자 비디오 강제 뮤트
   */
  muteRemoteVideo(userId: string): void {
    const room = this._room;
    if (!room) return;
    room.sendCommandOnce(CONFERENCE_COMMANDS.MASTER_MEDIA_CONTROL, {
      value: room.myUserId(),
      attributes: { action: 'mute', mediaType: 'video', target: userId },
    });
  }

  /**
   * 마스터 → 모든 참여자 오디오 뮤트 (커맨드 + 서버 강제 muteParticipant)
   */
  muteAllAudio(): void {
    const room = this._room;
    if (!room) return;
    room.sendCommandOnce(CONFERENCE_COMMANDS.MASTER_MEDIA_CONTROL, {
      value: room.myUserId(),
      attributes: { action: 'mute', mediaType: 'audio', target: 'all' },
    });
    // 서버 강제 뮤트 병행 (오디오만)
    const participants = room.getParticipants?.() ?? [];
    participants.forEach((p: { getId: () => string }) => {
      room.muteParticipant(p.getId(), 'audio' as never);
    });
  }

  /**
   * 마스터 → 모든 참여자 비디오 뮤트
   */
  muteAllVideo(): void {
    const room = this._room;
    if (!room) return;
    room.sendCommandOnce(CONFERENCE_COMMANDS.MASTER_MEDIA_CONTROL, {
      value: room.myUserId(),
      attributes: { action: 'mute', mediaType: 'video', target: 'all' },
    });
  }

  /**
   * 마스터 → 특정/전체 참여자에게 카메라/마이크 켜기 요청
   */
  requestUnmute(userId: string, mediaType: 'audio' | 'video'): void {
    const room = this._room;
    if (!room) return;
    room.sendCommandOnce(CONFERENCE_COMMANDS.MASTER_MEDIA_CONTROL, {
      value: room.myUserId(),
      attributes: { action: 'request_unmute', mediaType, target: userId },
    });
  }

  requestUnmuteAll(mediaType: 'audio' | 'video'): void {
    const room = this._room;
    if (!room) return;
    room.sendCommandOnce(CONFERENCE_COMMANDS.MASTER_MEDIA_CONTROL, {
      value: room.myUserId(),
      attributes: { action: 'request_unmute', mediaType, target: 'all' },
    });
  }

  /**
   * 뷰 모드·참여자 수·메인 스피커에 따라 수신 비디오 해상도를 동적으로 조절한다.
   *
   * 정책 (videoQualityPolicy.ts):
   *   - GRID: 화면 표시 수에 따라 ≤2→720 / ≤8→360 / 9+→180
   *   - PRESENTER/SIDE: 메인 720, 서브 리스트 180
   *   - overrides: 테스트 탭에서 수동 오버라이드 시 정책 대신 적용
   *   - sourceName 시그널링이 없으면 setReceiverVideoConstraint으로 폴백
   */
  updateReceiverQuality(
    screenMode: 'GRID' | 'SIDE' | 'PRESENTER',
    mainUserId: string | null,
    visibleCount: number,
    overrides?: { main?: number | null; sub?: number | null; gridTiers?: GridTier[] | null; gridFallback?: VideoQualityLevel | null },
  ): void {
    if (!this._room) return;

    console.log('%c[updateReceiverQuality]', 'color:#0af;font-weight:bold', { screenMode, visibleCount, mainUserId, overrides });

    const hasSources = this._remoteVideoSourceNames.size > 0 || this._remoteScreenSourceNames.size > 0;

    if (screenMode === 'GRID') {
      // 그리드뷰: 균일 해상도 (오버라이드 또는 정책)
      const maxHeight = overrides?.main
        ?? getGridResolution(
          visibleCount,
          overrides?.gridTiers ?? undefined,
          overrides?.gridFallback ?? undefined,
        );

      console.log('%c[updateReceiverQuality] GRID → maxHeight:', 'color:#0af;font-weight:bold', maxHeight);
      if (hasSources) {
        this._room.setReceiverConstraints({
          lastN: -1,
          onStageSources: [],          // ← SIDE/PRESENTER에서 남은 stage 힌트 제거
          defaultConstraints: { maxHeight },
          constraints: {},              // ← 개별 소스 constraint도 초기화
          // setReceiverConstraints는 constraints 객체를 통째로 교체하므로 매번 다시 실어야 유지된다
          ...(isRecorderMode() ? { assumedBandwidthBps: RECORDER_ASSUMED_BANDWIDTH_BPS } : {}),
        });
      } else {
        this._room.setReceiverVideoConstraint(maxHeight);
      }
    } else {
      // PRESENTER / SIDE: 메인 = 고해상도, 나머지 = 저해상도
      const mainHeight = overrides?.main ?? FOCUS_VIEW_POLICY.main;
      const subHeight = overrides?.sub ?? FOCUS_VIEW_POLICY.sub;
      const mainSource = mainUserId
        ? this._remoteVideoSourceNames.get(mainUserId)
        : undefined;
      const screenSources = Array.from(this._remoteScreenSourceNames.values());
      const constraints: Record<string, { maxHeight: number }> = {};
      const onStageSources = Array.from(new Set([
        ...(mainSource ? [mainSource] : []),
        ...screenSources,
      ]));

      if (mainSource) {
        constraints[mainSource] = { maxHeight: mainHeight };
      }
      for (const screenSource of screenSources) {
        constraints[screenSource] = { maxHeight: mainHeight };
      }

      console.log('%c[updateReceiverQuality] FOCUS → main:', 'color:#0af;font-weight:bold', mainHeight, 'sub:', subHeight, 'mainSource:', mainSource, 'screenSources:', screenSources);
      if (hasSources && onStageSources.length > 0) {
        this._room.setReceiverConstraints({
          lastN: -1,
          onStageSources,
          defaultConstraints: { maxHeight: subHeight },
          constraints,
          ...(isRecorderMode() ? { assumedBandwidthBps: RECORDER_ASSUMED_BANDWIDTH_BPS } : {}),
        });
      } else {
        // sourceName 미수신(P2P/구버전) → 단순 폴백
        this._room.setReceiverVideoConstraint(mainHeight);
      }
    }
  }

  selectParticipant(userId: string | null): void {
    // updateReceiverQuality가 해상도 정책을 관리하므로,
    // 여기서는 onStageSources 힌트만 전달한다.
    // 실제 해상도는 다음 useEffect 사이클에서 updateReceiverQuality가 결정.
    if (!this._room) return;
    // 의도적으로 setReceiverConstraints 호출하지 않음.
    // VideoRenewalPage의 useEffect가 mainUserId 변경을 감지하여
    // updateReceiverQuality를 올바른 오버라이드와 함께 호출한다.
    console.log('%c[selectParticipant]', 'color:#fa0;font-weight:bold', 'userId:', userId, '(no-op, deferred to updateReceiverQuality)');
  }

  // ─── 채팅 ────────────────────────────────────────────────────────────────

  sendChatMessage(message: string, to?: string): void {
    // 일반 채팅(voice/system JSON 제외)은 로컬 Redux에 즉시 추가 (원본 wehago-conference 동작)
    // MESSAGE_RECEIVED에서 isLocalUser는 스킵하므로 여기서 추가해야 화면에 표시됨
    let isVoice = false;
    let isSystem = false;
    try {
      const obj = JSON.parse(message);
      if (obj?.type === 'voice') isVoice = true;
      if (obj?.type === 'system') isSystem = true;
    } catch { /* plain text */ }

    const isPlainChat = !isVoice && !isSystem;

    if (isPlainChat && this._room) {
      const myId = this._room.myUserId();
      const local = store.getState().local;
      const localProfileUrl = toDisplayProfileUrl(local.profileUrl, local.avatarUrl) ?? '';
      store.dispatch(
        chatActions.messageReceived({
          id: `msg-${myId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          senderId: myId,
          senderName: local.nickname || local.userName || myId,
          message,
          timestamp: Date.now(),
          isPrivate: !!to,
          targetId: to,
          profileUrl: localProfileUrl,
          isMine: true,
        })
      );

      // 채팅 메시지 DB 적재 (type: 'text')
      saveMessageToDB(message, 'text');
    }

    if (to) {
      this._room?.sendMessage(message, to);
    } else {
      this._room?.sendMessage(message);
    }
  }

  // ─── 룸 커맨드 ───────────────────────────────────────────────────────────

  sendCommand(command: string, values: unknown): void {
    if (command === CONFERENCE_COMMANDS.WEHAGO_ID) {
      this._sendWehagoIdentityCommand(values);
      return;
    }

    this._room?.sendCommand(command, values);
  }

  sendCommandOnce(command: string, values: unknown): void {
    this._room?.sendCommandOnce(command, values);
  }

  removeCommand(command: string): void {
    this._room?.removeCommand(command);
  }

  // ─── 녹화 ────────────────────────────────────────────────────────────────

  async startRecording(options: unknown): Promise<void> {
    await this._room?.startRecording(options);
  }

  async stopRecording(sessionId: string): Promise<void> {
    await this._room?.stopRecording(sessionId);
  }

  // ─── 디바이스 ────────────────────────────────────────────────────────────

  async setAudioOutputDevice(deviceId: string): Promise<void> {
    await window.JitsiMeetJS.mediaDevices.setAudioOutputDevice(deviceId);
  }

  getRoom(): JitsiConference | null {
    return this._room;
  }

  getLocalAudioTrack(): JitsiLocalTrack | null {
    return this._localAudioTrack;
  }

  getLocalVideoTrack(): JitsiLocalTrack | null {
    return this._localVideoTrack;
  }

  // ─── 채팅 이름/ID 캐시 (localStorage) ────────────────────────────────────

  /** senderId → 표시 이름 캐시 저장 (재접속 후 이름 조회용) */
  private _saveSenderNameToCache(senderId: string, name: string): void {
    if (!name || name === senderId) return;
    try {
      const key = `wehago_chat_names_${this._roomId}`;
      const cache = JSON.parse(localStorage.getItem(key) || '{}') as Record<string, string>;
      cache[senderId] = name;
      localStorage.setItem(key, JSON.stringify(cache));
    } catch { /* ignore */ }
  }

  /** senderId에 대한 캐시된 이름 조회 */
  private _getSenderNameFromCache(senderId: string): string {
    try {
      const key = `wehago_chat_names_${this._roomId}`;
      const cache = JSON.parse(localStorage.getItem(key) || '{}') as Record<string, string>;
      return cache[senderId] ?? '';
    } catch { return ''; }
  }

  /** 내 과거 jitsiId 목록에 저장 (재접속 후 내 메시지 판별용) */
  private _saveMyJitsiId(jitsiId: string): void {
    try {
      const key = `wehago_my_jitsi_ids_${this._roomId}`;
      const ids = JSON.parse(localStorage.getItem(key) || '[]') as string[];
      if (!ids.includes(jitsiId)) {
        ids.push(jitsiId);
        localStorage.setItem(key, JSON.stringify(ids));
      }
    } catch { /* ignore */ }
  }

  /** senderId가 내 과거 jitsiId 중 하나인지 확인 */
  private _isMyHistoricalJitsiId(senderId: string): boolean {
    try {
      const key = `wehago_my_jitsi_ids_${this._roomId}`;
      const ids = JSON.parse(localStorage.getItem(key) || '[]') as string[];
      return ids.includes(senderId);
    } catch { return false; }
  }

  // ─── Dispose ─────────────────────────────────────────────────────────────

  async dispose(handlers?: ConferenceHandlers): Promise<void> {
    this._clearTranscriberStatusSyncTimer();

    // 오디오 레벨 타이머 정리
    this._audioLevelTimers.forEach((timer) => clearTimeout(timer));
    this._audioLevelTimers.clear();
    this._lastAudioLevelDispatch.clear();

    // 1. 회의 퇴장을 먼저 호출하여 XMPP 이벤트를 즉시 전송 (페이지 새로고침 시 ghost participant 방지)
    let leavePromise = Promise.resolve();
    if (this._room) {
      try {
        leavePromise = this._room.leave();
      } catch (err) {
        console.warn('[ConferenceConnector] leave error:', err);
      }
      this._room = null;
    }

    // 2. 로컬 트랙 리스트 추출
    const tracksToDispose = [
      this._localAudioTrack,
      this._localVideoTrack,
      this._localScreenTrack,
    ].filter((t): t is JitsiLocalTrack => t !== null && !t.disposed);

    this._localAudioTrack = null;
    this._localVideoTrack = null;
    this._localScreenTrack = null;

    this._handlers = null;

    if (handlers) {
      handlers.dispatch(participantsActions.reset());
      handlers.dispatch(audioIndicatorActions.reset());
      audioLevelStore.clearAll();
      handlers.dispatch(mainUserActions.reset());
      handlers.dispatch(screenShareActions.reset());
      handlers.dispatch(floorActions.reset());
      handlers.dispatch(recordingActions.reset());
    }

    // 3. leave를 먼저 완료 대기 (이후 Connection disconnect가 안전하게 수행됨)
    await leavePromise;

    // 4. 로컬 트랙 dispose (Chrome 146 CVE 보호: detach → 500ms 후 dispose)
    // Promise.all로 병렬 처리하여 딜레이 최소화
    await Promise.all(tracksToDispose.map(track => safeDisposeTrack(track)));
  }
}

export default ConferenceConnector;
